/**
 * WeaveIn.ai Platform - Member Profile Component (Tailwind CSS)
 * Enhanced view with referrals, enrollments, and data conflicts
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { onOpenRecord, parkRecord, takePendingRecord, type MemberWorkTab } from '@/lib/recordDeepLinks';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  ArrowLeft as ArrowLeftIcon,
  Pencil as EditIcon,
  Trash2 as DeleteIcon,
  ChevronDown as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  ChevronsLeft as ChevronsLeftIcon,
  ChevronsRight as ChevronsRightIcon,
  User as PersonIcon,
  UserCircle,
  RotateCcw,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Activity as LocalHospitalIcon,
  ShieldCheck as VerifiedUserIcon,
  Loader2,
  Shield as ShieldIcon,
  Eye,
  EyeOff,
  Star,
  Plus,
  Search as SearchIcon,
  X as XIcon,
  Printer as PrinterIcon,
  Network as NetworkIcon,
  Settings as SettingsIcon,
} from 'lucide-react';
import { ShieldedField, RevealStatusBar } from '../security/ShieldedField';
import { ReauthModal } from '../security/ReauthModal';
import { useDataShield } from '@/contexts/DataShieldContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import CareCoordinationTab from '@/components/members/CareCoordinationTab';
import ReturnToReportBanner from '@/components/reports/ReturnToReportBanner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import useMembers from '@/hooks/useMembers';
import useCustomFields from '@/hooks/useCustomFields';
import { Member, CustomField, Referral, Enrollment, DataConflict, UnifiedViewSettings, ProfileField, MemberBillingSummary, ProfileLayout, ProfileLayoutSection, ProfileLayoutTab, membersAPI } from '@/services/api/members';
import { ServiceReferralCreateForm } from '@/components/referral-hub/ServiceReferralCreateForm';
import { claimsApi } from '@/services/api/claims';
import { enrollmentsAPI } from '@/services/api/enrollments';
import ClaimLedgerView from '@/components/billing/ClaimLedgerView';
import EnrollmentStatusWidget from './EnrollmentStatusWidget';
import InlineCustomFieldEditor from './InlineCustomFieldEditor';
import { isFieldVisible } from '../forms/fieldVisibility';
import MemberFieldGroupsSection from './MemberFieldGroupsSection';
import CoordinatorSelector from './CoordinatorSelector';
import ProgramCaseAssignments from './ProgramCaseAssignments';
import { formatDateOnly, toDateInputValue } from '@/utils/dateTime';
import { isOffList } from '@/utils/reassignment';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import MedicaidEnrollmentForm from './MedicaidEnrollmentForm';
import MedicaidEnrollmentDetailModal from './MedicaidEnrollmentDetailModal';
import { MedicaidEnrollment } from '@/services/api/medicaidEnrollment';
import { ACCENT_BUTTON, DANGER_GHOST_BUTTON, INK_GHOST_BUTTON } from '../ui/accent';
import { MemberDataValidation } from '../retention/MemberDataValidation';
import MemberEnrichmentTab from '../enrichment/MemberEnrichmentTab';
import MemberEngagementHistoryTab from './MemberEngagementHistoryTab';
import MemberWorkflowsTab from './MemberWorkflowsTab';
import MemberEngagementIntelligenceTab from './MemberEngagementIntelligenceTab';
import MemberConsentPanel from '../consent/MemberConsentPanel';
import MemberFfsContinuityPanel from './MemberFfsContinuityPanel';
import { MemberBenefitsTab } from '../benefits/MemberBenefitsTab';
import { usePageCapabilities } from '@/hooks/usePageCapability';
import { useCapability } from '@/hooks/useCapabilities';
import { MemberEnrollmentsTab } from '../health-plans/MemberEnrollmentsTab';
import FamilyMembersTab from './FamilyMembersTab';
import MemberHouseholdsTab from './MemberHouseholdsTab';
import CaregiversTab from './CaregiversTab';
import ExternalReferralsPanel from './ExternalReferralsPanel';
import ReferToNetworkOrgModal from './ReferToNetworkOrgModal';
import MemberWorkforceTab from './MemberWorkforceTab';
import { isBehavioralAssessmentAllowed } from '../behavioral-assessment/orgGate';
import { useOrganization } from '@/contexts/OrganizationContext';
import { userManagementAPI } from '@/services/api/userManagement';
import MemberHistoryPanel from './MemberHistoryPanel';
import MemberProgramWorkspace from './MemberProgramWorkspace';
import MemberProgramsTab from './MemberProgramsTab';
import CategoryNotesSection from './CategoryNotesSection';
import { MemberFormsHub } from '@/components/forms/MemberFormsHub';
import { memberActivityFeedAPI, type NoteCategory } from '@/services/api/memberActivityFeed';
import { useMemberFavorites } from '@/hooks/useMemberFavorites';
import MemberWorkComplianceTab from './MemberWorkComplianceTab';
import { MemberNotificationsTab } from '@/components/ecmReminders/MemberNotificationsTab';
import MemberAchievementsPanel from '@/components/incentives/MemberAchievementsPanel';
import AddEditMemberNew from './AddEditMemberNew';
import DonorGivingTab from '@/components/donors/DonorGivingTab';
import { getDonor } from '@/services/api/donorProfiles';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  normalizeProfileSections,
  resolveProfileTabRequest,
  segmentTabKey,
  tabMeta,
} from './profileNav';

import { config as productConfig } from '@/src/config';
import PrintPacketDialog from './print/PrintPacketDialog';
/**
 * Placeholder shown on non-demographics tabs when creating a new member.
 * Tells the user to save demographics first, then these tabs become active.
 */
function CreateModeGate({ tabName, onGoBack }: { tabName: string; onGoBack: () => void }) {
  const { t } = useAppTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <UserCircle className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
      <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
        {tabName}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mb-4">
        {t('members.saveDemographicsFirst', "Save the member's demographics first. Once the member is created, this section will become available.")}
      </p>
      <button
        type="button"
        onClick={onGoBack}
        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
      >
        {t('members.goToMemberProfile', 'Go to Member Profile')}
      </button>
    </div>
  );
}

/** The tab whose content the header's Edit button edits (TRUS-762). */
export const PROFILE_EDIT_TAB = 'demographics';

/**
 * The header's "Edit Member Profile" button. It opens the demographics editor,
 * so it shows only while Member Profile is the active tab — never on Programs,
 * Activities, Referrals, Billing, Engagement or a Member setup entry, each of
 * which has its own edit path. The caller keeps the permission checks
 * (not view-only, not create mode); this adds only the tab rule.
 */
export function MemberProfileEditButton({ activeTab, onEdit }: { activeTab: string; onEdit: () => void }) {
  const { t } = useAppTranslation();
  if (activeTab !== PROFILE_EDIT_TAB) return null;
  return (
    <Button
      variant="primary"
      icon={<EditIcon size={16} />}
      className={ACCENT_BUTTON}
      onClick={onEdit}
    >
      {t('buttons.edit', 'Edit Member Profile')}
    </Button>
  );
}

interface MemberProfileProps {
  memberId?: number;
  onNavigate?: (tab: string, memberId?: number) => void;
}

// A few core fields are written to the member API (PUT /api/members/{id},
// MemberBase) under a name that differs from their profile-fields *registry*
// key. Submitting under the registry key makes the backend silently drop the
// value — Pydantic ignores unknown keys, so the request returns 200 and the
// field just never persists. That was the "I can't add the DOB" report
// (TRUS-542): date_of_birth showed, appeared editable, saved without error,
// and the value vanished. This mirrors the same registry-key → field map
// AddEditMemberNew uses for its create/edit form; keep the two in sync.
//
// Preferred Language is NOT remapped (TRUS-738): it is stored, shown and used
// by auto-translate as `preferred_language`. Mapping it to `language` saved
// every edit to a column the profile and auto-translate never read.
export const CORE_FIELD_WRITE_KEY: Record<string, string> = {
  date_of_birth: 'dob',
};
export const coreFieldWriteKey = (fieldKey: string): string =>
  CORE_FIELD_WRITE_KEY[fieldKey] ?? fieldKey;

// Section-edit input renderer — type-aware control for a single ProfileField.
// Used inside the per-section Edit mode; renders text/date/datetime/number/
// select/checkbox/textarea variants based on pf.field_type. For custom-field
// rows it also reads field_options from the CustomField definition (where
// available) so select inputs get the right enumeration.
export function SectionEditField({
  pf,
  value,
  onChange,
  customField,
  fieldsUnavailable = false,
  onRetryFields,
}: {
  pf: ProfileField;
  value: any;
  onChange: (v: any) => void;
  customField?: CustomField;
  /** The field DEFINITIONS failed to load (see useCustomFields). Without this we
   *  cannot tell "this field has no options" from "we never received the
   *  options", and we render the first message for the second cause. */
  fieldsUnavailable?: boolean;
  onRetryFields?: () => void;
}) {
  const { t } = useAppTranslation();
  const type = (pf.field_type || 'str').toLowerCase();
  const inputClass =
    'w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500';
  const labelEl = (
    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
      {pf.display_name}
      {pf.is_required && <span className="text-red-500 ml-1">*</span>}
    </div>
  );

  // Read-only fields (e.g. org-configured derived/system fields like the
  // referral status field) must not render an editable control in section
  // edit mode. Show the current value as static text so it can't be changed.
  if (pf.is_readonly) {
    return (
      <div>
        {labelEl}
        <div className="text-sm px-2 py-1 text-gray-700 dark:text-gray-300">
          {value !== undefined && value !== null && value !== '' ? String(value) : '-'}
        </div>
      </div>
    );
  }

  // Dropdown options. The AUTHORITATIVE source is the profile-fields payload
  // (pf.field_options) — GET /api/member-field-config/profile-fields resolves the
  // org override falling back to the field's own options precisely so a consumer
  // can render the input (TRUS-401/403, migration 667). The flat custom-fields list
  // (customField.field_options, fetched separately via useCustomFields) is only a
  // FALLBACK: it lags behind, caches transient errors as [], and can omit a field,
  // and when it did a configured multi_select rendered "No options are configured
  // for this field" even though the org has options (TRUS-501/502). Prefer pf, fall
  // back to customField. Shape varies in prod: string[] or {label,value}[].
  const selectOptions: Array<{ label: string; value: string }> = (() => {
    const pfOpts = (pf as any)?.field_options;
    const cfOpts = (customField as any)?.field_options;
    const raw = Array.isArray(pfOpts) && pfOpts.length ? pfOpts : cfOpts;
    if (!Array.isArray(raw)) return [];
    return raw.map((o: any) =>
      typeof o === 'string' ? { label: o, value: o } : { label: o.label ?? o.value, value: o.value ?? o.label }
    );
  })();

  // The options for this control live on the CustomField definition, which is
  // fetched separately from the field list itself. When that fetch fails the
  // control is still rendered (the list came from the profile-fields endpoint)
  // but has nothing to offer — say so, and give the user a way out that is not
  // "reload the whole page" (TRUS-501/502/503).
  const optionsFailedToLoad =
    fieldsUnavailable && selectOptions.length === 0 &&
    (type === 'select' || type === 'radio' || type === 'multi_select');

  if (optionsFailedToLoad) {
    return (
      <div>
        {labelEl}
        <div className="flex items-center gap-2 text-xs px-2 py-1 rounded border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950">
          <span className="text-amber-800 dark:text-amber-200">
            {t('members.fieldSettingsUnavailable', "Couldn't load this field's settings.")}
          </span>
          {onRetryFields && (
            <button
              type="button"
              onClick={onRetryFields}
              className="underline font-medium text-amber-900 dark:text-amber-100 hover:no-underline focus:outline-none focus:ring-2 focus:ring-amber-500 rounded"
            >
              {t('common.retry', 'Retry')}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (type === 'bool' || type === 'toggle') {
    return (
      <div>
        {labelEl}
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded"
          />
          <span>{value ? t('common.yes', 'Yes') : t('common.no', 'No')}</span>
        </label>
      </div>
    );
  }
  if (type === 'date' || type === 'fixed_date') {
    return (
      <div>
        {labelEl}
        <input
          type="date"
          value={toDateInputValue(value)}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      </div>
    );
  }
  if (type === 'datetime') {
    return (
      <div>
        {labelEl}
        <input
          type="datetime-local"
          value={value ? String(value).slice(0, 16) : ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        />
      </div>
    );
  }
  if (type === 'int') {
    return (
      <div>
        {labelEl}
        <input
          type="number"
          min={0}
          value={value ?? ''}
          // Reject negatives: empty -> null, otherwise clamp to >= 0.
          onChange={(e) => {
            if (e.target.value === '') return onChange(null);
            const n = Number(e.target.value);
            onChange(Number.isNaN(n) ? null : Math.max(0, n));
          }}
          onKeyDown={(e) => {
            // Block the minus key outright so the field never shows a negative.
            if (e.key === '-' || e.key === 'Subtract') e.preventDefault();
          }}
          className={inputClass}
        />
        {typeof value === 'number' && value < 0 && (
          <div className="text-xs text-red-500 mt-1">{t('members.valueMustBeZeroOrGreater', 'Value must be 0 or greater.')}</div>
        )}
      </div>
    );
  }
  if ((type === 'select' || type === 'radio') && selectOptions.length > 0) {
    return (
      <div>
        {labelEl}
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        >
          <option value="">{t('members.selectPlaceholder', '-- Select --')}</option>
          {/* Keep a value that is no longer on the list (e.g. a DCM who left) visible
              rather than showing the placeholder; it cannot be picked again (TRUS-716). */}
          {isOffList({ field_options: selectOptions }, value) && (
            <option value={String(value)} disabled>
              {t('members.offListOption', '{{value}} (no longer on the list)', { value: String(value) })}
            </option>
          )}
          {selectOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    );
  }
  if (type === 'multi_select') {
    // Render a CHECKBOX group, consistent with the Add/Edit member form and the inline
    // profile editor (TRUS-289). This branch is intentionally NOT gated on
    // selectOptions.length: a multi_select with no options must never fall through to
    // the free-text input below — that turned a constrained field into a text box.
    const selectedValues: string[] = (() => {
      try {
        const p = typeof value === 'string' ? JSON.parse(value) : value;
        return Array.isArray(p) ? p : [];
      } catch {
        return [];
      }
    })();
    if (selectOptions.length === 0) {
      return (
        <div>
          {labelEl}
          <div className="text-xs text-gray-500 dark:text-gray-400 italic px-2 py-1">
            {t('members.noOptionsConfigured', 'No options are configured for this field.')}
          </div>
        </div>
      );
    }
    const toggleValue = (val: string) => {
      const next = selectedValues.includes(val)
        ? selectedValues.filter((v) => v !== val)
        : [...selectedValues, val];
      onChange(JSON.stringify(next));
    };
    return (
      <div>
        {labelEl}
        <div className="flex flex-col gap-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-900 max-h-72 overflow-y-auto">
          {selectOptions.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selectedValues.includes(o.value)}
                onChange={() => toggleValue(o.value)}
                className="rounded"
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }
  // Long text: multi-line textarea (e.g. a survivor story), not a single-line box.
  // Match InlineCustomFieldEditor's heuristic (TRUS-296): the explicit `long_text`
  // type always gets the textarea, and a plain `str` field configured with a large
  // str_len (> 255, e.g. "Survivor Story" at str_len 12000) does too — legacy data
  // configured before the long_text type existed. Without the str_len case a long
  // narrative rendered through the cramped single-line <input> in section edit
  // (TRUS-500: "Text box is very small, please increase from a line to a 5x5 box").
  // str_len lives on the field definition (customField), not on the ProfileField.
  const isLongText = type === 'long_text' || (type === 'str' && ((customField as any)?.str_len ?? 0) > 255);
  if (isLongText) {
    return (
      <div>
        {labelEl}
        <textarea
          value={value ?? ''}
          placeholder={pf.placeholder_text || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={6}
          className={`${inputClass} resize-y min-h-[140px] leading-relaxed`}
        />
      </div>
    );
  }
  // Default + str + select-without-options: free-text input.
  return (
    <div>
      {labelEl}
      <input
        type="text"
        value={value ?? ''}
        placeholder={pf.placeholder_text || ''}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
    </div>
  );
}

/** Horizontal sub-tab bar used to consolidate related member tabs into one. */
function SubTabBar({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ id: string; label: string }>;
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2 mb-4 border-b border-gray-200 dark:border-gray-700 pb-2">
      {tabs.map((tb) => (
        <button
          key={tb.id}
          type="button"
          onClick={() => onChange(tb.id)}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
            value === tb.id
              ? 'bg-v2accent text-white'
              : 'border border-v2border bg-surface text-ink-500 hover:text-ink'
          }`}
        >
          {tb.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Whether the "Billing & Reimbursement" section should be visible on the member
 * profile. It requires BOTH the org `billing` capability to be enabled AND the
 * per-org `show_billing` view flag to not be explicitly disabled. Previously the
 * nav only consulted the view flag, so it stayed visible for orgs that had the
 * billing capability turned off (TRUS-352). `show_billing === undefined` counts
 * as visible (default-on view flag), matching the other member-profile sections.
 */
export function isBillingSectionVisible(
  orgHasBilling: boolean,
  showBillingFlag: boolean | undefined,
): boolean {
  return orgHasBilling && showBillingFlag !== false;
}

export interface LayoutLabelSource {
  label: string;
  label_override: string | null;
  i18n_key: string;
  default_label: string;
}

/**
 * Which of the two label sources to show for one nav element (TRUS-528).
 *
 * An element the org left alone goes through i18n. That is not a detail: the
 * clients' own bundles already carry a complete translation of all 6 section
 * headings and ~30 tab names, and the server's tenant overlay deliberately
 * does NOT duplicate them. Preferring the payload's `label` unconditionally
 * would therefore hand a Spanish-preference caseworker the English default —
 * a silent regression, because an unconfigured org's payload is byte-identical
 * to one from before this feature existed.
 *
 * An element the org DID rename uses the payload: that string is the org's own
 * wording, already translated server-side if the org has Spanish for it, and
 * no i18n bundle can know it. Note-category tabs (TRUS-420) carry no i18n key
 * and are always the org's own words.
 */
export function resolveLayoutLabel(
  el: LayoutLabelSource,
  translate: (key: string, fallback: string) => string,
): string {
  if (el.label_override) return el.label;
  if (!el.i18n_key) return el.label;
  return translate(el.i18n_key, el.default_label);
}

/**
 * Last-resort nav tree for when GET /profile-layout does not answer.
 *
 * The server owns this tree (app/services/member_profile/tab_registry.py) and
 * every normal render comes from there — which is what lets an org rename and
 * reorder it (TRUS-528). This mirror exists only so a failed request degrades
 * to a working profile instead of an empty rail; the failure is reported to
 * observability rather than swallowed, which is the lesson useNavConfig's
 * silent FALLBACK_SECTIONS taught.
 *
 * Deliberately minimal: no org overrides (there is no payload to read them
 * from) and `visible: true` throughout, because the per-tab gates that DO have
 * client-side inputs still apply via `tabExtras`. Labels go through i18n like
 * any unrenamed element.
 */
const FALLBACK_PROFILE_SECTIONS: ProfileLayoutSection[] = (
  [
    ['identity', 'Identity', 'members.sectionIdentity', [
      ['demographics', 'Member Profile', 'members.demographics'],
      ['family', 'Family Members', 'members.familyMembers'],
      ['caregivers', 'Caregivers', 'members.caregivers'],
      ['households', 'Households', 'members.households'],
    ]],
    // TRUS-762: Enrollments and Doula stay in the tree (profileNav marks them
    // as aliases of Programs, so they are never listed) for parity with the
    // server registry; Family, Caregivers, Households and the Data / Compliance
    // tabs default to the Member setup sheet.
    ['care', 'Care', 'members.sectionCare', [
      ['enrollments', 'Enrollments', 'programs.enrollments'],
      ['programs', 'Programs', 'members.programs'],
      ['billing', 'Billing & Reimbursement', 'members.billing'],
      ['care-coordination', 'Referrals & Care Coordination', 'members.referralsAndCoordination'],
      ['external-referrals', 'Outside Referrals', 'members.externalReferrals'],
      ['health-plan', 'Health Plan Coverage', 'members.healthPlanCoverage'],
      ['doula-journey', 'Doula', 'members.doulaJourney'],
      ['benefits', 'Benefits', 'members.benefits'],
    ]],
    ['program_activities', 'Casework', 'members.sectionProgramActivities', [
      ['program-activities', 'Activities', 'members.activitiesAndCases'],
    ]],
    ['data', 'Data', 'members.sectionData', [
      ['history', 'History & Conflicts', 'members.historyAndConflicts'],
      ['ai-enrichment', 'Member Data Enrichment', 'members.aiEnrichment'],
      ['consent', 'Consent', 'members.consent'],
      ['data-validation', 'Data Validation', 'members.dataValidation'],
      ['system-information', 'System Information', 'members.systemInformation'],
    ]],
    ['compliance', 'Compliance', 'members.sectionCompliance', [
      ['notifications', 'Notifications', 'members.notifications'],
      ['work-compliance', 'Work Compliance', 'members.workCompliance'],
      ['workforce', 'Workforce', 'workforce.profile'],
    ]],
    ['engagement', 'Engagement', 'members.sectionEngagement', [
      ['engagement', 'Engagement', 'members.engagement'],
      ['engagement-intel', 'Engagement Intelligence', 'members.engagementIntelligence'],
      ['engagements', 'Campaigns & Engagements', 'members.campaignsAndEngagements'],
      ['achievements', 'Incentives & Badges', 'members.incentivesAndBadges'],
      ['giving', 'Giving', 'members.giving'],
    ]],
  ] as Array<[string, string, string, Array<[string, string, string]>]>
).map(([sectionKey, label, i18nKey, tabs], si) => ({
  element_key: sectionKey,
  section_key: sectionKey,
  label,
  default_label: label,
  label_override: null,
  i18n_key: i18nKey,
  display_order: (si + 1) * 10,
  default_order: (si + 1) * 10,
  is_customized: false,
  tabs: tabs.map(([tabKey, tabLabel, tabI18n], ti) => ({
    element_key: tabKey,
    tab_key: tabKey,
    section_key: sectionKey,
    parent_tab_key: null,
    label: tabLabel,
    default_label: tabLabel,
    label_override: null,
    i18n_key: tabI18n,
    display_order: (ti + 1) * 10,
    default_order: (ti + 1) * 10,
    is_customized: false,
    settings_columns: [],
    data_gated: false,
    always_visible: false,
    visible: true,
    sub_tabs: [],
  })),
}));

/**
 * Enrollment statuses that mean the member has LEFT the program, so the
 * enrollment no longer contributes a "current program" to scope the profile to.
 * Every other status — including 'pending', the status a new enrollment gets by
 * default — means the member is currently in that program for field-scoping
 * purposes (TRUS-507). Compared case-insensitively.
 */
const TERMINATED_ENROLLMENT_STATUSES = new Set([
  'completed',
  'cancelled',
  'canceled',
  'withdrawn',
]);

/**
 * Pick the program context to auto-apply on the member profile so per-program
 * field/category overrides (program_field_visibility) take effect on load.
 *
 * The profileProgramId context was always meant to be "driven by the member's
 * enrollments" but nothing wired it up, so admin-configured per-program hides
 * were silently ignored until a staffer manually chose a program in the
 * "View as program" dropdown — a Promotora-only member kept seeing Navigator
 * fields the org had hidden for Promotora (TRUS-485).
 *
 * Returns the program_id when the member has exactly ONE current (non-terminated)
 * program enrollment; returns null for zero or multiple current programs,
 * preserving the unified cross-enrollment view (a member genuinely in both
 * programs should see both programs' fields). An enrollment with no status is
 * treated as current.
 *
 * "Current" means anything except a terminated status
 * (see TERMINATED_ENROLLMENT_STATUSES). Requiring the status to be literally
 * 'active' silently broke the auto-scope for the common case: the enrollment
 * endpoint assigns enrollment_status='pending' by default
 * (app/routers/enrollments.py), so a freshly-enrolled Promotora-only member kept
 * seeing the Navigator fields the org had hidden for Promotora — the same
 * symptom TRUS-485 fixed, still reproducing for pending enrollments (TRUS-507).
 */
export function pickAutoProfileProgramId(
  enrollments: Array<{ program_id?: number; status?: string; enrollment_status?: string }>,
): number | null {
  const activeProgramIds = Array.from(
    new Set(
      enrollments
        .filter((e) => {
          const status = ((e?.status ?? e?.enrollment_status) || 'active')
            .toString()
            .toLowerCase();
          return !TERMINATED_ENROLLMENT_STATUSES.has(status);
        })
        .map((e) => e.program_id)
        .filter((pid): pid is number => typeof pid === 'number'),
    ),
  );
  return activeProgramIds.length === 1 ? activeProgramIds[0] : null;
}

/**
 * The value map that conditional visibility is evaluated against while rendering
 * the configured member-profile fields.
 *
 * Outside edit mode this is simply the saved `member.custom_fields`. But while a
 * section is in per-section EDIT mode, visibility must track the LIVE draft, not
 * the persisted record: a field gated on another field's answer (e.g. "FEMA
 * registration ID Number", shown only once "FEMA Registration ID" is answered
 * Y/N) must appear the moment the parent answer changes in the draft — not only
 * after the section is Saved and reopened (TRUS-598). We therefore overlay the
 * editing section's unsaved drafts on top of the saved values.
 *
 * `isCustom` guards the merge: `sectionDrafts` is keyed by field_key and also
 * holds CORE fields under their registry key, which do not belong in the
 * custom-field value map the evaluator reads; only custom drafts are overlaid.
 *
 * This mirrors the inline single-field save path (`saveCustomField`), which
 * already updates `member.custom_fields` in place so conditional fields
 * re-evaluate live — the section editor is the one write path that did not.
 */
export function visibilityValuesForSectionEdit(
  savedValues: Record<string, any>,
  editingSection: string | null,
  sectionDrafts: Record<string, any>,
  isCustom: (fieldKey: string) => boolean,
): Record<string, any> {
  if (!editingSection) return savedValues;
  const merged: Record<string, any> = { ...savedValues };
  for (const [key, value] of Object.entries(sectionDrafts || {})) {
    if (isCustom(key)) merged[key] = value;
  }
  return merged;
}

export default function MemberProfileNew({ memberId: initialMemberId, onNavigate }: MemberProfileProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const { getMember, deleteMember, restoreMember } = useMembers({ autoFetch: false });
  const { fields, fieldsUnavailable, refetch: refetchCustomFields } = useCustomFields();

  // Internal member ID — starts from prop, but updates in-place after creation
  const [memberId, setMemberId] = useState<number | undefined>(initialMemberId);
  const isCreateMode = !memberId;
  const { isShieldEnabled, isRevealed, loadEffectivePolicy, requestReveal } = useDataShield();

  // Helper: check if a specific field is shielded (from backend _shielded_fields metadata)
  const isFieldInShield = (m: Member | null, fieldName: string): boolean =>
    (m?._shielded_fields || []).includes(fieldName);

  // Page capabilities for conditional tab rendering
  const orgId = parseInt(localStorage.getItem('active_cbo_id') || '0', 10) || null;
  const { results: pageAccess, loading: capabilitiesLoading } = usePageCapabilities(
    orgId,
    ['workforce', 'medicaid.enrollment']
  );
  const orgHasWorkforce = pageAccess['workforce']?.has_access ?? false;
  const hasMedicaidEnrollment = pageAccess['medicaid.enrollment']?.has_access ?? false;

  // Billing & Reimbursement is an org capability (see tabAccessRegistry `capability: "billing"`).
  // The member-profile nav previously gated it only on the `show_billing` view flag, so it kept
  // showing for orgs with the `billing` capability disabled (TRUS-352). Gate it on the capability
  // too, matching how ProgramManagement and the tab-access registry treat billing.
  const { enabled: orgHasBilling } = useCapability(orgId, 'billing');

  // Recovery Plan is a disaster long-term-recovery concept, not a universal one,
  // so the section is feature-gated and OFF by default (migration 677 enables it
  // for Maui LTRG only). The API 403s without the capability too — this just
  // keeps the tab out of the nav for everyone else.
  const { enabled: orgHasRecoveryPlan } = useCapability(orgId, 'recovery_plan');

  // Outside Referrals is likewise a disaster-long-term-recovery concept, not a universal
  // one, so it is feature-gated and OFF by default — an admin switches it on per org from
  // Roles & Permissions -> Feature Capabilities, no deploy needed (TRUS-511). The API
  // 403s without the capability too; this keeps the tab out of the nav for everyone else.
  const { enabled: orgHasExternalReferrals } = useCapability(orgId, 'external_referrals');
  // An org either runs a doula programme or it does not. Gating on the
  // capability rather than on the member's own data keeps the nav rail
  // stable: the rail renders before a member is chosen, so a member-scoped
  // query would either block it or flash the tab in and out.
  const { enabled: orgHasDoulaProgram } = useCapability(orgId, 'doula_program');
  // FFS transition "After 1 Jan 2027" panel (TRUS-729) is sensitive (it flags
  // members by an aid-code proxy for UIS membership), so it is gated on its own
  // org capability, off by default (TRUS-739). The API 403s without it too.
  const { enabled: orgHasFfsTransition } = useCapability(orgId, 'ffs_transition');

  // Note categories an admin flagged `show_as_section` each get their own tab,
  // listing the notes tagged with them (TRUS-420). Categories are per-CBO, so
  // this is inherently org-scoped — an org sees sections only for categories it
  // defined itself. The Recovery Plan section additionally requires the
  // recovery_plan capability, keeping it Maui-only as originally scoped.
  const [sectionCategories, setSectionCategories] = useState<NoteCategory[]>([]);
  useEffect(() => {
    let cancelled = false;
    memberActivityFeedAPI
      .listCategories()
      .then((res) => {
        if (cancelled) return;
        setSectionCategories(
          (res.items || []).filter(
            (c) =>
              c.is_active &&
              c.show_as_section &&
              (c.slug !== 'recovery_plan' || orgHasRecoveryPlan)
          )
        );
      })
      .catch(() => {
        // Non-fatal: no sections rather than a broken profile.
        if (!cancelled) setSectionCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId, orgHasRecoveryPlan]);

  // Behavioral Assessment is a pilot feature gated to specific orgs
  // (Sycamores / Trust Care). Only show the Assessments tab for allowed orgs.
  const { selectedOrganization } = useOrganization();
  const showAssessmentsTab = isBehavioralAssessmentAllowed(
    orgId,
    selectedOrganization?.name,
  );

  const [member, setMember] = useState<Member | null>(null);
  // TRUS-732: a program stores its case manager on the member's Case Manager
  // field, so ProgramCaseAssignments already shows and edits it.
  const [caseManagerViaProgram, setCaseManagerViaProgram] = useState(false);
  const [loading, setLoading] = useState(true);
  const [memberPlan, setMemberPlan] = useState<{
    lob_name: string | null;
    lob_type: string | null;
    product_name: string | null;
    product_code: string | null;
    plan_kind: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('demographics');
  // TRUS-762: the Engagement hub's selected segment (a segment's tab key), a
  // request to open the doula journey inside Programs, and the Member setup sheet.
  const [engagementSegment, setEngagementSegment] = useState<string | null>(null);
  const [doulaRequest, setDoulaRequest] = useState<{ at: number } | null>(null);
  const [setupOpen, setSetupOpen] = useState(false);
  /** Rows on the Programs list once it has loaded (the nav badge). */
  const [programsCount, setProgramsCount] = useState<number | null>(null);
  /** The layout's alias map, read by openTab (the layout state is declared below). */
  const layoutAliasesRef = useRef<Record<string, { tab_key: string; segment: string | null }> | null>(null);
  /**
   * Open a tab by any key it has ever had (TRUS-762): Enrollments and Doula
   * open Programs (Doula with its journey showing); the old Engagement tabs
   * open the Engagement hub on their segment. Everything else opens itself.
   */
  const openTab = (key: string) => {
    const { tab, segment } = resolveProfileTabRequest(key, layoutAliasesRef.current);
    if (tab === 'engagement') setEngagementSegment(segmentTabKey(segment));
    if (tab === 'programs' && segment === 'doula') setDoulaRequest({ at: Date.now() });
    setActiveTab(tab);
  };
  // Anything that still sets an old key directly lands on its new home.
  useEffect(() => {
    if (resolveProfileTabRequest(activeTab, layoutAliasesRef.current).tab !== activeTab) openTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  // TRUS-685: a link like /members-view?id=12&tab=tasks opens Program
  // activities on that work tab. `at` makes a repeat request re-apply.
  const [requestedWorkTab, setRequestedWorkTab] = useState<{ tab: MemberWorkTab; at: number } | null>(null);
  const linkMemberIdRef = useRef(memberId);
  linkMemberIdRef.current = memberId;
  useEffect(() => {
    const apply = (workTab: MemberWorkTab | null, profileTab?: string) => {
      if (!workTab) {
        // TRUS-762: a profile tab, by its current key or an old one.
        if (profileTab) openTab(profileTab);
        return;
      }
      setActiveTab('program-activities');
      setRequestedWorkTab({ tab: workTab, at: Date.now() });
    };
    const parked = takePendingRecord('member');
    if (parked && parked.memberId === memberId) apply(parked.workTab, parked.profileTab);
    else if (parked) parkRecord(parked); // for the member about to be shown
    return onOpenRecord('member', (link) => {
      if (link.memberId !== linkMemberIdRef.current) return false;
      apply(link.workTab, link.profileTab);
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);
  const [printOpen, setPrintOpen] = useState(false);
  // Donor-ness is a role on the record (donor_profiles) OR derived from having
  // given, so the Giving tab appears for anyone the org treats as a donor.
  const [isDonorRecord, setIsDonorRecord] = useState(false);

  // Sub-tabs for consolidated tabs:
  //   care-coordination  → Care Coordination | Referrals | Workflows
  //   history            → Conflicts | Audit History
  // (Documents/Forms/Conversations sub-tabs now live in MemberProgramWorkspace.
  //  Case History moved there too: every case, past ones included, is a chip in
  //  its case bar with its details and history — TRUS-762.)
  const [careSub, setCareSub] = useState<'referrals' | 'coordination' | 'workflows'>('coordination');
  const [dataSub, setDataSub] = useState<'conflicts' | 'history'>('conflicts');

  // Workforce tab is shown only when both:
  //  - the org has workforce enabled (page capability), AND
  //  - this member is classified as workforce (has a member_workforce_profile row)
  const showWorkforceTab = orgHasWorkforce && (member?.is_workforce ?? false);

  // Role PREVIEW testing (TRUS-308): when a super_admin/ops_admin is previewing a
  // role (sidebar preview → localStorage 'sidebar_preview_role'), reflect that
  // role's member-edit capability here so they can test edit-vs-view for the role.
  // Privileged callers otherwise always resolve to 'full', so preview would never
  // show a view-only role as view-only. Read-only affordance — the real write gate
  // is unchanged on the backend.
  const [previewRole, setPreviewRole] = useState<string | null>(
    () => localStorage.getItem('sidebar_preview_role') || null
  );
  const [previewCanEdit, setPreviewCanEdit] = useState<boolean | null>(null);
  const [previewRoleLabel, setPreviewRoleLabel] = useState<string | null>(null);

  // Donor lookup — cheap, and a non-donor simply returns is_donor=false.
  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    getDonor(Number(memberId))
      .then((d) => !cancelled && setIsDonorRecord(!!d?.is_donor))
      .catch(() => !cancelled && setIsDonorRecord(false));
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  useEffect(() => {
    const onStorage = () => setPreviewRole(localStorage.getItem('sidebar_preview_role') || null);
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!previewRole || !orgId) {
      setPreviewCanEdit(null);
      setPreviewRoleLabel(null);
      return;
    }
    let cancelled = false;
    userManagementAPI
      .getPreviewMemberEditCapability(orgId, { roleType: previewRole })
      .then((cap) => {
        if (cancelled) return;
        setPreviewCanEdit(cap.can_edit_members);
        setPreviewRoleLabel(cap.display_name_en || previewRole);
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewCanEdit(null);
          setPreviewRoleLabel(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [previewRole, orgId]);

  // Effective edit access for the current user on this member. 'view_only'
  // means the user can see this member (typically via a program they're
  // assigned to) but is not the assigned case manager / staff for this member.
  // While previewing a role, fold in that role's member-edit capability so the
  // edit affordances reflect the previewed role rather than the admin's own access.
  const isViewOnly =
    member?.access_level === 'view_only' || (previewRole != null && previewCanEdit === false);

  // Favorite-member toggle (shared with MemberListNew via the same hook).
  const { isFavorite, toggleFavorite } = useMemberFavorites();

  // New state for referrals, enrollments, and conflicts
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [showReferralForm, setShowReferralForm] = useState(false);
  // TRUS-749: send the member to another org's Referral Inbox.
  const [showNetworkReferral, setShowNetworkReferral] = useState(false);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [conflicts, setConflicts] = useState<DataConflict[]>([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [loadingConflicts, setLoadingConflicts] = useState(false);
  const [billingSummary, setBillingSummary] = useState<MemberBillingSummary | null>(null);
  const [loadingBilling, setLoadingBilling] = useState(false);
  const [billingLedgerOpen, setBillingLedgerOpen] = useState<number | null>(null);
  const [billingBusy, setBillingBusy] = useState<number | null>(null);
  const [billingPending, setBillingPending] = useState<{ id: number; enrollment_id: number; activity_name: string }[]>([]);
  const [billingDraftNote, setBillingDraftNote] = useState<Record<string, any> | null>(null);
  const [billingDrafting, setBillingDrafting] = useState(false);
  const [billingResetting, setBillingResetting] = useState(false);

  // Unified view settings control which sub-panels are visible per org
  const [viewSettings, setViewSettings] = useState<UnifiedViewSettings | null>(null);
  // Server-resolved nav tree (sections/tabs, this org's wording and order).
  // null = not loaded or the request failed; the rail falls back to
  // FALLBACK_PROFILE_SECTIONS so it is never empty. (TRUS-528)
  const [profileLayout, setProfileLayout] = useState<ProfileLayout | null>(null);
  layoutAliasesRef.current = profileLayout?.aliases ?? null;

  // The work-tab bar inside "Activities & Cases" is a sub-tab list on the
  // `program-activities` tab, so its wording and order travel in the same
  // payload as the rail. Mapped to the ids MemberProgramWorkspace uses.
  // Only an org's OWN wording is forwarded — a tab left at the platform
  // default keeps going through the workspace's own t(), which already has a
  // complete translation for it.
  const workspaceTabConfig = useMemo(() => {
    const workspace = profileLayout?.sections
      .flatMap(sec => sec.tabs)
      .find(tab => tab.tab_key === 'program-activities');
    const config: Record<string, { label?: string; order?: number }> = {};
    for (const sub of workspace?.sub_tabs ?? []) {
      if (!sub.label_override && sub.display_order === sub.default_order) continue;
      config[sub.tab_key] = {
        label: sub.label_override ? sub.label : undefined,
        order: sub.display_order,
      };
    }
    return config;
  }, [profileLayout]);

  // Per-org core-field hide/show config (mirrors AddEditMemberNew so this
  // profile view actually respects "Hide field" toggles set in Field Config
  // Manager). Without this, hardcoded blocks like Medicaid ID / CIN render
  // regardless of admin config — TRUS-49 follow-up.
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  // Optional program context: when set, the profile reflects that program's
  // per-program field overrides (program_field_visibility) instead of the org
  // base. Null = org default. Driven by the member's enrollments below.
  const [profileProgramId, setProfileProgramId] = useState<number | null>(null);
  useEffect(() => {
    membersAPI.getProfileFields({
      include_hidden: true,
      program_id: profileProgramId ?? undefined,
    })
      .then(setProfileFields)
      .catch(() => setProfileFields([]));
  }, [profileProgramId]);

  // Auto-scope the profile to the member's program so per-program field
  // overrides (program_field_visibility) actually apply on load. The
  // profileProgramId context was designed to be "driven by the member's
  // enrollments" (see comment above) but nothing wired it up, so
  // admin-configured per-program field/category hides were silently ignored
  // on the default profile — a Promotora-only member still saw Navigator
  // fields the org had hidden for Promotora (TRUS-485). When the member has
  // exactly one active program enrollment we default to it; zero or multiple
  // active programs keep the org-default unified view (null). A manual
  // "View as program" choice wins and is not overridden — the auto-default
  // runs at most once per member.
  const autoProgramAppliedFor = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (autoProgramAppliedFor.current === memberId) return;
    if (enrollments.length === 0) return;
    autoProgramAppliedFor.current = memberId;
    const autoProgramId = pickAutoProfileProgramId(enrollments);
    if (autoProgramId !== null) {
      setProfileProgramId(autoProgramId);
    }
  }, [enrollments, memberId]);

  // Seq-1 Demographics UX: hide-empty toggle (suppress fields with no value)
  // and collapse-empty (categories with zero filled fields default collapsed).
  // Persisted per-user in localStorage so case managers' preference survives.
  const [hideEmptyFields, setHideEmptyFields] = useState<boolean>(() => {
    try {
      return localStorage.getItem('member_profile_hide_empty') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('member_profile_hide_empty', hideEmptyFields ? '1' : '0');
    } catch {}
  }, [hideEmptyFields]);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('member_profile_collapsed_cats');
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const toggleCategoryCollapsed = (key: string) => {
    setCollapsedCats(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try {
        localStorage.setItem('member_profile_collapsed_cats', JSON.stringify(Array.from(next)));
      } catch {}
      return next;
    });
  };

  // Left-sidebar nav section collapse (Identity / Care / Activity / …).
  // Persisted so a case manager who only uses a couple of sections isn't
  // forced to scroll past every group on each visit. Default: all expanded.
  // Keyed on section_key, not the visible heading — an org renaming a section
  // (TRUS-528) would otherwise lose the user's collapse state for it.
  const [collapsedNavSections, setCollapsedNavSections] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem('member_profile_collapsed_nav');
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });
  const toggleNavSection = (sectionKey: string) => {
    setCollapsedNavSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey); else next.add(sectionKey);
      try {
        localStorage.setItem('member_profile_collapsed_nav', JSON.stringify(Array.from(next)));
      } catch {}
      return next;
    });
  };
  // Collapse the entire left tab rail to a thin strip so the content area can
  // use the full page width. Persisted across visits.
  const [navRailCollapsed, setNavRailCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('member_profile_nav_rail_collapsed') === '1';
    } catch {
      return false;
    }
  });
  const toggleNavRail = () => {
    setNavRailCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('member_profile_nav_rail_collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Seq-1 follow-on: scoped field search across the Demographics tab.
  // Case-insensitive match on display name or field key; the matched
  // category opens automatically when a match exists inside it.
  const [fieldSearch, setFieldSearch] = useState<string>('');

  // Per-section edit (replacing the whole-record Edit Demographics page).
  // editingSection holds the category_key of the section currently in edit
  // mode (only one at a time, so saves are contained). sectionDrafts mirrors
  // the form state for that section; on Save we POST a diff via updateMember
  // and refresh `member` from the response.
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [sectionDrafts, setSectionDrafts] = useState<Record<string, any>>({});
  const [sectionSaving, setSectionSaving] = useState<boolean>(false);

  const enterSectionEdit = (categoryKey: string, items: ProfileField[]) => {
    if (!member) return;
    // Seed draft with current values for every field in the section so that
    // unchanged inputs reflect existing data and `diff` works cleanly.
    const seed: Record<string, any> = {};
    for (const pf of items) {
      seed[pf.field_key] = pf.is_core_field
        ? coreFieldValue(pf.field_key)
        : (member.custom_fields || {})[pf.field_key];
    }
    setSectionDrafts(seed);
    setEditingSection(categoryKey);
  };

  const cancelSectionEdit = () => {
    setSectionDrafts({});
    setEditingSection(null);
  };

  const saveSection = async (items: ProfileField[]) => {
    if (!member) return;
    setSectionSaving(true);
    try {
      // Build a diff against the loaded member so we only PUT changed fields.
      const corePayload: Record<string, any> = {};
      // Read-side mirror of the core changes, keyed by the profile-fields
      // registry key so coreFieldValue() reflects the save immediately (the
      // request payload is keyed by the write-schema name instead).
      const coreLocalUpdates: Record<string, any> = {};
      const customFieldsPayload: Array<{ field_name: string; value: any }> = [];
      for (const pf of items) {
        // Never submit read-only fields, even if a draft value exists.
        if (pf.is_readonly) continue;
        const draft = sectionDrafts[pf.field_key];
        const current = pf.is_core_field
          ? coreFieldValue(pf.field_key)
          : (member.custom_fields || {})[pf.field_key];
        // Coerce both sides to comparable scalars; treat undefined/null/'' as equal.
        const normalize = (v: any) => (v === undefined || v === null ? '' : v);
        if (normalize(draft) === normalize(current)) continue;
        if (pf.is_core_field) {
          const value = draft === '' ? null : draft;
          // Translate registry key → member write-schema field name so the
          // backend does not silently drop it (TRUS-542).
          corePayload[coreFieldWriteKey(pf.field_key)] = value;
          coreLocalUpdates[pf.field_key] = value;
        } else {
          customFieldsPayload.push({ field_name: pf.field_key, value: draft ?? '' });
        }
      }
      if (Object.keys(corePayload).length === 0 && customFieldsPayload.length === 0) {
        cancelSectionEdit();
        return;
      }
      const body: any = { ...corePayload };
      if (customFieldsPayload.length) body.custom_fields = customFieldsPayload;
      const baseUrl = (productConfig.weaveinApi.baseUrl ?? '').replace(/\/$/, '');
      const token = localStorage.getItem('auth_token') || '';
      const cboId = localStorage.getItem('active_cbo_id') || '';
      // Offline-aware: queues + optimistically caches when offline (desktop),
      // runs the real PUT when online (and on web).
      const { offlineAwareMutate } = await import('@/lib/offline/hooks');
      const result: any = await offlineAwareMutate(
        'UPDATE',
        'member-profile',
        String(member.id),
        body,
        async () => {
          const resp = await fetch(`${baseUrl}/api/members/${member.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'X-CBO-ID': cboId,
            },
            body: JSON.stringify(body),
          });
          if (!resp.ok) {
            const errText = await resp.text().catch(() => '');
            throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200)}`);
          }
          return resp.json().catch(() => ({}));
        },
      );
      // Apply local state (also reflects the optimistic offline save). Write
      // both the API field name and the registry-key alias so coreFieldValue()
      // (which reads e.g. date_of_birth) shows the new value right away.
      Object.assign(member, corePayload, coreLocalUpdates);
      if (customFieldsPayload.length && member.custom_fields) {
        for (const { field_name, value } of customFieldsPayload) {
          member.custom_fields[field_name] = value;
        }
      }
      cancelSectionEdit();
      if (result && typeof result === 'object' && result.queued) {
        alert(t('members.savedOffline', 'Saved offline — will sync when you reconnect.'));
      }
    } catch (e: any) {
      alert(t('members.saveFailedX', 'Save failed: {{error}}', { error: e.message || e }));
    } finally {
      setSectionSaving(false);
    }
  };
  const isCoreFieldHidden = (fieldKey: string): boolean => {
    const config = profileFields.find(f => f.is_core_field && f.field_key === fieldKey);
    return config?.is_hidden ?? false;
  };

  // Medicaid Enrollment state
  const [showEnrollmentForm, setShowEnrollmentForm] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<MedicaidEnrollment | null>(null);
  const [showEnrollmentDetail, setShowEnrollmentDetail] = useState(false);
  const [enrollmentKey, setEnrollmentKey] = useState(0); // Key to force widget refresh

  // Sync internal memberId when prop changes (e.g. navigating between members)
  useEffect(() => {
    if (initialMemberId) setMemberId(initialMemberId);
  }, [initialMemberId]);

  useEffect(() => {
    if (memberId) {
      loadMember(memberId);
      // Fetch this member's LOB/Plan from the canonical enrollment lookup.
      const cboId = localStorage.getItem('active_cbo_id') || '';
      if (cboId) {
        const url = `/api/orgs/${cboId}/members-plan-lookup`;
        fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
            'X-CBO-ID': cboId,
          },
        })
          .then(r => r.ok ? r.json() : [])
          .then((rows: Array<{ member_id: number; lob_name: string | null; lob_type: string | null; product_name: string | null; product_code: string | null; plan_kind: string | null }>) => {
            const match = (Array.isArray(rows) ? rows : []).find(r => r.member_id === memberId);
            setMemberPlan(match ? {
              lob_name: match.lob_name,
              lob_type: match.lob_type,
              product_name: match.product_name,
              product_code: match.product_code,
              plan_kind: match.plan_kind,
            } : null);
          })
          .catch(() => setMemberPlan(null));
      }
    }
    // Load view settings (controls which sub-panels are visible). When we have
    // a member, load the EFFECTIVE settings (org merged with the member's
    // active-program overrides, most-permissive). Before a member is resolved
    // (e.g. create mode), fall back to the org defaults.
    if (memberId) {
      membersAPI.getEffectiveViewSettings(memberId)
        .then(setViewSettings)
        .catch(() => {
          // Fall back to org settings if the effective merge is unavailable.
          membersAPI.getUnifiedViewSettings().then(setViewSettings).catch(() => {});
        });
    } else {
      membersAPI.getUnifiedViewSettings()
        .then(setViewSettings)
        .catch(() => {}); // Defaults to all visible if not configured
    }

    // The nav tree itself: which sections and tabs exist, what this org calls
    // them, and what order they are in (TRUS-528). Same member_id merge as the
    // view settings above, so a program override reaches both.
    let layoutCancelled = false;
    membersAPI.getProfileLayout(memberId || undefined)
      .then((layout) => {
        if (!layoutCancelled) setProfileLayout(layout);
      })
      .catch((err: any) => {
        // Degrade to FALLBACK_PROFILE_SECTIONS rather than an empty rail, but
        // REPORT it — a silently-degraded nav is the exact failure mode
        // useNavConfig's fallback used to hide (observability_issues → RCA).
        if (!layoutCancelled) setProfileLayout(null);
        import('@/src/lib/activityTracker')
          .then((m) =>
            m.trackError({
              error_type: 'ProfileLayoutFetchFailed',
              error_message: `profile-layout fetch failed, using fallback tree: ${err?.message || String(err)}`,
              path: '/api/member-field-config/profile-layout',
              status_code: err?.response?.status ?? err?.status,
              method: 'GET',
              stack: err?.stack,
            }),
          )
          .catch(() => {});
      });
    return () => { layoutCancelled = true; };
  }, [memberId]);

  const loadMember = async (memberId: number) => {
    setLoading(true);
    setError(null);
    const data = await getMember(memberId);
    if (data) {
      setMember(data);
      // Load effective privacy policy for this member (for shield context)
      const cboId = parseInt(localStorage.getItem('active_cbo_id') || '0', 10);
      if (cboId) {
        loadEffectivePolicy(cboId, memberId).catch(() => {});
      }
    } else {
      setError(t('members.memberNotFound', 'Member not found'));
    }
    setLoading(false);
  };

  const loadReferrals = async (memberId: number) => {
    setLoadingReferrals(true);
    try {
      const data = await membersAPI.getMemberReferrals(memberId);
      setReferrals(data);
    } catch (error) {
      console.error('Error loading referrals:', error);
    } finally {
      setLoadingReferrals(false);
    }
  };

  const loadEnrollments = async (memberId: number) => {
    console.log('🔍 Loading enrollments for member:', memberId);
    setLoadingEnrollments(true);
    try {
      const data = await membersAPI.getMemberEnrollments(memberId);
      console.log('✅ Enrollments loaded:', data.length, 'records');
      setEnrollments(data);
    } catch (error) {
      console.error('❌ Error loading enrollments:', error);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const loadConflicts = async (memberId: number) => {
    setLoadingConflicts(true);
    try {
      const data = await membersAPI.getMemberConflicts(memberId);
      setConflicts(data);
    } catch (error) {
      console.error('Error loading conflicts:', error);
    } finally {
      setLoadingConflicts(false);
    }
  };

  const loadBilling = async (memberId: number) => {
    setLoadingBilling(true);
    try {
      const data = await membersAPI.getMemberBillingSummary(memberId);
      setBillingSummary(data);
    } catch (error) {
      console.error('Error loading billing summary:', error);
    }
    // Pending billable activities (documented services not yet billed).
    try {
      const r = await claimsApi.getMemberPendingBillableActivities(memberId);
      setBillingPending(
        (r.activities || []).map((a) => ({
          id: a.activity_id,
          enrollment_id: a.enrollment_id,
          activity_name: a.activity_name,
        }))
      );
    } catch {
      setBillingPending([]);
    } finally {
      setLoadingBilling(false);
    }
  };

  const completeAndBill = async (enrollmentId: number, activityId: number) => {
    setBillingBusy(activityId);
    try {
      await enrollmentsAPI.completeActivity(enrollmentId, activityId);
      if (memberId) await loadBilling(memberId);
    } catch (e) {
      console.error('complete & bill failed', e);
    } finally {
      setBillingBusy(null);
    }
  };

  const draftProgressNote = async () => {
    if (!memberId) return;
    setBillingDrafting(true);
    try {
      const res = await claimsApi.draftProgressNote(memberId);
      setBillingDraftNote(res.note || null);
    } catch (e) {
      console.error('draft progress note failed', e);
    } finally {
      setBillingDrafting(false);
    }
  };

  const resetBillingDemo = async () => {
    if (!memberId) return;
    setBillingResetting(true);
    try {
      await claimsApi.resetBillingDemo(memberId);
      setBillingDraftNote(null);
      await loadBilling(memberId);
    } catch (e) {
      console.error('reset billing demo failed', e);
    } finally {
      setBillingResetting(false);
    }
  };

  // Load tab data when tab changes
  useEffect(() => {
    if (!memberId) return;

    if (activeTab === 'care-coordination' && referrals.length === 0) {
      loadReferrals(memberId);
    } else if (activeTab === 'case-episodes' && enrollments.length === 0) {
      loadEnrollments(memberId);
    } else if (activeTab === 'history' && conflicts.length === 0) {
      loadConflicts(memberId);
    } else if (activeTab === 'billing' && !billingSummary) {
      loadBilling(memberId);
    }
  }, [activeTab, memberId]);

  // The member's enrollments are read on load, not only when a tab asks: the
  // Programs badge and the auto program scope both need them (TRUS-762 folded
  // the Enrollments tab, the only thing that used to load them, into Programs).
  useEffect(() => {
    if (!memberId) return;
    setProgramsCount(null);
    loadEnrollments(memberId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const handleDelete = async () => {
    if (!member) return;

    // Say what actually happens. This is an ARCHIVE — nothing is destroyed and it can be
    // undone — and the old wording ("delete") is why staff believed data was gone and raised
    // support tickets to get records back (TRUS-371).
    if (window.confirm(t(
      'members.confirmArchiveMember',
      'Archive {{name}}? They will be removed from your member list, but nothing is deleted — you can restore them from their profile afterwards.',
      { name: `${member.first_name} ${member.last_name}` },
    ))) {
      const success = await deleteMember(member.id);
      if (success) {
        onNavigate?.('members');
      }
    }
  };

  const isArchived = (member?.membership_status || '').toLowerCase() === 'inactive';

  const handleRestore = async () => {
    if (!member) return;
    const success = await restoreMember(member.id);
    if (success) {
      // Reload so the banner clears and the profile reflects the live record.
      const fresh = await getMember(member.id);
      if (fresh) setMember(fresh);
    }
  };

  const formatFieldValue = (field: CustomField, value: any): string => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (field.type) {
      case 'bool':
        return value ? t('common.yes', 'Yes') : t('common.no', 'No');
      case 'date':
      case 'fixed_date':
        return fmt.short(value);
      case 'datetime':
        return fmt.dateTime(value);
      case 'time':
        return value;
      default:
        return String(value);
    }
  };

  const getStatusVariant = (status: string): 'default' | 'primary' | 'secondary' | 'danger' | 'warning' | 'info' | 'success' => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'completed':
        return 'success';
      case 'pending':
      case 'on hold':
        return 'warning';
      case 'in progress':
        return 'info';
      default:
        return 'default';
    }
  };

  const getQualityScoreVariant = (score: number): 'danger' | 'warning' | 'success' => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  const handleStartEnrollment = () => {
    setShowEnrollmentForm(true);
  };

  const handleEnrollmentSuccess = () => {
    setShowEnrollmentForm(false);
    // Force widget to refresh
    setEnrollmentKey((prev) => prev + 1);
  };

  const handleViewEnrollment = (enrollment: MedicaidEnrollment) => {
    setSelectedEnrollment(enrollment);
    setShowEnrollmentDetail(true);
  };

  // Field keys that carry PII and must route through ShieldedField in the
  // data-driven layout.
  const PII_CORE_FIELDS = new Set([
    'first_name', 'last_name', 'date_of_birth', 'dob', 'email', 'phone_no',
    'street_address', 'city', 'state', 'zip_code',
    'medicaid_id', 'medicare_id', 'commercial_insurance_id', 'patient_no', 'ssn_last_four',
  ]);

  // Resolve a core field's display value from the loaded member, handling the
  // few keys whose column name differs from the field_key.
  const coreFieldValue = (key: string): any => {
    if (!member) return undefined;
    switch (key) {
      case 'phone_no': return (member as any).phone_number || (member as any).phone_no;
      case 'date_of_birth': return formatDateOnly((member as any).date_of_birth || (member as any).dob);
      // TRUS-738: one column. No fallback to `language` — what shows here is
      // what is saved and what auto-translate uses.
      case 'preferred_language': return (member as any).preferred_language;
      default: return (member as any)[key];
    }
  };

  // Data-driven Demographics: render every configured, visible field in the
  // admin's Profile Builder category + sort order, interleaving core and
  // custom fields. family_member / household categories are managed in their
  // own tabs and excluded here. Used only when the org has profile-field
  // config; otherwise renderCoreInfo falls back to the legacy fixed layout.
  // Treat null/undefined/empty-string/empty-array/empty-object as "empty"
  // for the per-field saturation and hide-empty toggle.
  const isProfileFieldEmpty = (pf: ProfileField): boolean => {
    if (!member) return true;
    const v = pf.is_core_field ? coreFieldValue(pf.field_key) : (member.custom_fields || {})[pf.field_key];
    if (v === undefined || v === null || v === '') return true;
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'object') return Object.keys(v).length === 0;
    return false;
  };

  /** A section that is not a field category (Data Validation, System
   *  Information) but sits in the same column and table of contents. Collapsed
   *  by default: its key in collapsedCats means EXPANDED. */
  type ExtraSection = { key: string; name: string; icon?: React.ReactNode; render: () => React.ReactNode };
  const isExtraExpanded = (key: string) => collapsedCats.has(key);

  const renderConfiguredFields = (extras: ExtraSection[] = []) => {
    if (!member) return null;
    const customByName = new Map<string, CustomField>(fields.map((f: any) => [f.name, f]));
    const customValues: Record<string, any> = member.custom_fields || {};

    // While a section is being edited, evaluate conditional visibility against
    // the live draft (merged over the saved values) so a dependent field appears
    // as soon as its parent answer changes — not only after Save + reopen
    // (TRUS-598). Outside edit mode this is just `customValues`.
    const visibilityValues = visibilityValuesForSectionEdit(
      customValues,
      editingSection,
      sectionDrafts,
      (fieldKey) => customByName.has(fieldKey),
    );

    // Conditional visibility: a custom field with visibility_rules only shows
    // when its conditions are met by the member's current values (same
    // evaluator the forms use). Without this, conditional fields like
    // "Post Disaster Housing On Island" rendered unconditionally on the
    // profile even when the parent value didn't select them.
    const isConfiguredFieldVisible = (pf: ProfileField): boolean => {
      if (pf.is_core_field) return true;
      const cf = customByName.get(pf.field_key);
      if (!cf?.visibility_rules) return true;
      return isFieldVisible(
        { field_id: cf.name, visibility_rules: cf.visibility_rules as any },
        [],
        visibilityValues,
      );
    };

    type Group = { key: string; name: string; sort: number; items: ProfileField[]; filled: number };
    const groups: Group[] = [];
    const byKey = new Map<string, Group>();
    // Dedupe: a field can appear in profileFields under more than one category
    // (e.g. its configured category AND a com_fields.category text that matches a
    // configured section — the TRUS-193/197 "duplicate section" bug). Render each
    // field ONCE, in the first category it appears under; groups are created lazily
    // below, so a category whose fields were all already placed never renders.
    const seenKeys = new Set<string>();
    for (const pf of profileFields) {
      if (pf.is_hidden) continue;
      if (pf.category_key === 'family_member' || pf.category_key === 'household') continue;
      if (!isConfiguredFieldVisible(pf)) continue;
      if (seenKeys.has(pf.field_key)) continue;
      seenKeys.add(pf.field_key);
      let g = byKey.get(pf.category_key);
      if (!g) {
        g = { key: pf.category_key, name: pf.category_name || pf.category_key, sort: pf.category_sort ?? 999, items: [], filled: 0 };
        byKey.set(pf.category_key, g);
        groups.push(g);
      }
      g.items.push(pf);
      if (!isProfileFieldEmpty(pf)) g.filled += 1;
    }
    groups.sort((a, b) => a.sort - b.sort);
    // Within each category: required fields first (so case managers see
    // what MUST be filled before the optional fields), then by admin sort.
    for (const g of groups) {
      g.items.sort((a, b) => {
        const ra = a.is_required ? 0 : 1;
        const rb = b.is_required ? 0 : 1;
        if (ra !== rb) return ra - rb;
        return (a.field_sort ?? 0) - (b.field_sort ?? 0);
      });
    }

    // In-profile field search — match against display name + field key.
    const searchTerm = fieldSearch.trim().toLowerCase();
    const fieldMatchesSearch = (pf: ProfileField): boolean => {
      if (!searchTerm) return true;
      return (
        pf.display_name?.toLowerCase().includes(searchTerm) ||
        pf.field_key?.toLowerCase().includes(searchTerm)
      );
    };
    const matchedGroupKeys = new Set<string>();
    if (searchTerm) {
      for (const g of groups) {
        if (g.items.some(fieldMatchesSearch)) matchedGroupKeys.add(g.key);
      }
    }

    // Empty categories default to collapsed unless the user has explicitly
    // expanded them this session. User-explicit collapse/expand wins via the
    // collapsedCats Set (which tracks user OVERRIDES, not effective state).
    // Search overrides everything: matched groups always expand.
    const isCategoryCollapsed = (g: Group): boolean => {
      if (searchTerm && matchedGroupKeys.has(g.key)) return false;
      if (searchTerm && !matchedGroupKeys.has(g.key)) return true;
      const userToggled = collapsedCats.has(g.key);
      const defaultCollapsed = g.filled === 0;
      return userToggled ? !defaultCollapsed : defaultCollapsed;
    };

    return (
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sticky TOC — collapses to a horizontal scroller on narrow screens.
            Click jumps to the section. */}
        <aside className="md:w-44 lg:w-64 md:sticky md:top-4 md:self-start md:max-h-[calc(100vh-6rem)] md:overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                {t('members.onThisPage', 'On this page')}
              </div>
              <button
                type="button"
                onClick={() => setHideEmptyFields(v => !v)}
                className="text-xs flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                title={hideEmptyFields ? t('members.showEmptyFields', 'Show empty fields') : t('members.hideEmptyFields', 'Hide empty fields')}
              >
                {hideEmptyFields ? <EyeOff size={12} /> : <Eye size={12} />}
                <span>{hideEmptyFields ? t('members.emptyHidden', 'Empty hidden') : t('members.showAll', 'Show all')}</span>
              </button>
            </div>

            {/* Field search — narrows TOC + auto-opens matching categories.
                Defers to the global Cmd-K only when search term is empty. */}
            <div className="relative mb-2">
              <SearchIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={fieldSearch}
                onChange={(e) => setFieldSearch(e.target.value)}
                placeholder={t('members.findAField', 'Find a field...')}
                className="w-full pl-7 pr-7 py-1 text-xs border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {fieldSearch && (
                <button
                  type="button"
                  onClick={() => setFieldSearch('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-0.5"
                  title={t('members.clear', 'Clear')}
                >
                  <XIcon size={11} />
                </button>
              )}
            </div>
            {searchTerm && (
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-1 px-1">
                {matchedGroupKeys.size === 1
                  ? t('members.sectionMatch', '{{count}} section match', { count: matchedGroupKeys.size })
                  : t('members.sectionsMatch', '{{count}} sections match', { count: matchedGroupKeys.size })}
              </div>
            )}
            <nav className="flex flex-col gap-0.5">
              {groups.filter(g => !searchTerm || matchedGroupKeys.has(g.key)).map((g) => {
                return (
                  <a
                    key={g.key}
                    href={`#cat-${g.key}`}
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(`cat-${g.key}`);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        // If category is collapsed, expand it on click so the
                        // user actually sees content after the scroll lands.
                        if (isCategoryCollapsed(g)) toggleCategoryCollapsed(g.key);
                      }
                    }}
                    className="group block px-2 py-1.5 rounded text-sm hover:bg-paper-2"
                  >
                    <span className="block truncate text-ink-500 group-hover:text-ink">
                      {g.name}
                    </span>
                  </a>
                );
              })}
              {!searchTerm && extras.map((x) => (
                <a
                  key={x.key}
                  href={`#cat-${x.key}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(`cat-${x.key}`);
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      if (!isExtraExpanded(x.key)) toggleCategoryCollapsed(x.key);
                    }
                  }}
                  className="group block px-2 py-1.5 rounded text-sm hover:bg-paper-2"
                >
                  <span className="block truncate text-ink-500 group-hover:text-ink">{x.name}</span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Section cards — collapsible header; empty cats start collapsed.
            With an active search, only matched categories render. */}
        <div className="flex-1 min-w-0 flex flex-col gap-6">
          {groups.filter(g => !searchTerm || matchedGroupKeys.has(g.key)).map((g) => {
            const collapsed = isCategoryCollapsed(g);
            const visibleItems = (hideEmptyFields ? g.items.filter(pf => !isProfileFieldEmpty(pf)) : g.items)
              .filter(fieldMatchesSearch);
            const inEdit = editingSection === g.key;
            return (
              <Card key={g.key} id={`cat-${g.key}`} className="scroll-mt-4">
                <CardContent className="p-6">
                  <div className="w-full flex items-center justify-between mb-2 gap-2">
                    <button
                      type="button"
                      onClick={() => !inEdit && toggleCategoryCollapsed(g.key)}
                      className="flex-1 flex items-center gap-2 min-w-0 group text-left"
                      aria-expanded={!collapsed}
                      disabled={inEdit}
                    >
                      {collapsed && !inEdit
                        ? <ChevronRightIcon size={18} className="text-gray-500 shrink-0" />
                        : <ExpandMoreIcon size={18} className="text-gray-500 shrink-0" />}
                      <h3 className="text-lg font-semibold truncate">{g.name}</h3>
                    </button>
                    <div className="flex items-center gap-2 shrink-0">
                      {!inEdit && (
                        <>
                          {!isViewOnly && !collapsed && (
                            <button
                              type="button"
                              onClick={() => enterSectionEdit(g.key, g.items)}
                              className="text-xs flex items-center gap-1 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:text-blue-600 hover:border-blue-400"
                              title={t('members.editSection', 'Edit {{name}}', { name: g.name })}
                              disabled={editingSection !== null}
                            >
                              <EditIcon size={12} />
                              <span className="hidden sm:inline">{t('members.edit', 'Edit')}</span>
                            </button>
                          )}
                        </>
                      )}
                      {inEdit && (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            onClick={() => saveSection(g.items)}
                            disabled={sectionSaving}
                          >
                            {sectionSaving ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                            {t('members.save', 'Save')}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={cancelSectionEdit}
                            disabled={sectionSaving}
                          >
                            {t('members.cancel', 'Cancel')}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {(!collapsed || inEdit) && (
                    <>
                      <Separator className="mb-6" />
                      {inEdit ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                          {g.items.map((pf) => (
                            <SectionEditField
                              key={pf.field_key}
                              pf={pf}
                              value={sectionDrafts[pf.field_key]}
                              onChange={(v) => setSectionDrafts(prev => ({ ...prev, [pf.field_key]: v }))}
                              customField={customByName.get(pf.field_key)}
                              fieldsUnavailable={fieldsUnavailable}
                              onRetryFields={refetchCustomFields}
                            />
                          ))}
                        </div>
                      ) : visibleItems.length === 0 ? (
                        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                          {hideEmptyFields
                            ? t('members.allFieldsEmpty', 'All fields empty — toggle "Show all" in the sidebar to see them.')
                            : t('members.noFieldsConfigured', 'No fields configured.')}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                          {visibleItems.map((pf) => {
                            if (!pf.is_core_field) {
                              const cf = customByName.get(pf.field_key);
                              // Read-only configured fields render as static text
                              // (no inline edit affordance) even for custom fields.
                              if (cf && !pf.is_readonly) {
                                return (
                                  <InlineCustomFieldEditor
                                    key={pf.field_key}
                                    field={cf}
                                    value={customValues[pf.field_key]}
                                    memberId={member.id}
                                    onSave={saveCustomFieldInline}
                                    recordValues={customValues}
                                  />
                                );
                              }
                              if (cf && pf.is_readonly) {
                                return (
                                  <div key={pf.field_key} className="min-w-0">
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{pf.display_name}</div>
                                    <div className="text-sm break-words">
                                      {customValues[pf.field_key] !== undefined && customValues[pf.field_key] !== null && customValues[pf.field_key] !== ''
                                        ? String(customValues[pf.field_key]) : '-'}
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <div key={pf.field_key} className="min-w-0">
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{pf.display_name}</div>
                                  <div className="text-sm break-words">{customValues[pf.field_key] ?? '-'}</div>
                                </div>
                              );
                            }
                            const val = coreFieldValue(pf.field_key);
                            return (
                              <div key={pf.field_key} className="min-w-0">
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{pf.display_name}</div>
                                <div className="text-sm break-words">
                                  {PII_CORE_FIELDS.has(pf.field_key) ? (
                                    <ShieldedField value={val} fieldName={pf.field_key} isShielded={isFieldInShield(member, pf.field_key)} />
                                  ) : (
                                    val !== undefined && val !== null && val !== '' ? String(val) : '-'
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {!searchTerm && extras.map((x) => {
            const open = isExtraExpanded(x.key);
            return (
              <Card key={x.key} id={`cat-${x.key}`} className="scroll-mt-4">
                <CardContent className="p-6">
                  <button
                    type="button"
                    onClick={() => toggleCategoryCollapsed(x.key)}
                    className="w-full flex items-center gap-2 text-left group"
                    aria-expanded={open}
                  >
                    {open
                      ? <ExpandMoreIcon size={18} className="text-ink-500 shrink-0" />
                      : <ChevronRightIcon size={18} className="text-ink-500 shrink-0" />}
                    {x.icon}
                    <h3 className="text-lg font-semibold truncate">{x.name}</h3>
                  </button>
                  {open && (
                    <>
                      <Separator className="my-6" />
                      {x.render()}
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  /** Data Validation and System Information, as collapsible sections (collapsed by default). */
  const dataValidationSection = (): ExtraSection | null => member ? ({
    key: '__data_validation_open__',
    name: t('members.dataValidation', 'Data Validation'),
    icon: <VerifiedUserIcon className="text-ink-500 shrink-0" size={20} />,
    render: () => (
      <MemberDataValidation
        member={{
          member_id: member.id,
          first_name: member.first_name || '',
          last_name: member.last_name || '',
          phone_number: member.phone_number || member.phone_no,
          email: member.email,
          address: member.street_address
            ? { street1: member.street_address, city: member.city || '', state: member.state || '', zip_code: member.zip_code || '' }
            : undefined,
        }}
        compact={false}
        onDataUpdated={() => {
          if (memberId) loadMember(memberId);
        }}
      />
    ),
  }) : null;

  const systemInfoSection = (): ExtraSection | null => member ? ({
    key: '__system_info_open__',
    name: t('members.systemInformation', 'System Information'),
    render: () => (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('common.created', 'Created')}</div>
          <div className="text-sm">
            {member.created_at ? fmt.dateTime(member.created_at) : member.created_ts ? fmt.dateTime(member.created_ts) : '-'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('common.lastUpdated', 'Last Updated')}</div>
          <div className="text-sm">
            {member.updated_at ? fmt.dateTime(member.updated_at) : member.last_updated_ts ? fmt.dateTime(member.last_updated_ts) : '-'}
          </div>
        </div>
      </div>
    ),
  }) : null;

  const renderCoreInfo = () => {
    if (!member) return null;

    // When the org actively uses the Profile Builder (i.e. has at least one
    // visible custom field in a profile category), render the Demographics tab
    // data-driven so the admin's field order, categories, and hide toggles are
    // honored. Orgs that only have default core fields fall through to the
    // polished legacy fixed layout — this keeps the blast radius to orgs that
    // have actually built a custom profile (e.g. Maui DCM).
    const useConfigured = profileFields.some(
      (f) => !f.is_core_field && !f.is_hidden && f.category_key !== 'family_member' && f.category_key !== 'household'
    );

    if (useConfigured) {
      // The Overview card only earns its space when there is a plan to surface.
      const showOverview = !!(memberPlan && (memberPlan.lob_name || memberPlan.product_name));
      return (
        <div className="flex flex-col gap-6">
          {memberId && !isViewOnly && !caseManagerViaProgram && (
            <div className="flex flex-wrap items-center gap-3">
              <CoordinatorSelector
                memberId={memberId}
                currentCoordinatorId={(member as any).primary_coordinator_id}
                onChanged={(newId) => {
                  if (member) (member as any).primary_coordinator_id = newId;
                }}
              />
            </div>
          )}
          {memberId && (
            <ProgramCaseAssignments memberId={memberId} readOnly={isViewOnly} onLoaded={setCaseManagerViaProgram} />
          )}
          {enrollments.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500 dark:text-gray-400">{t('members.viewAsProgram', 'View as program:')}</span>
              <select
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800"
                value={profileProgramId ?? ''}
                onChange={(e) => setProfileProgramId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">{t('members.allProgramsOrgDefault', 'All programs (org default)')}</option>
                {Array.from(
                  new Map(enrollments.map((en) => [en.program_id, en.program_name || `Program ${en.program_id}`])).entries()
                ).map(([pid, name]) => (
                  <option key={pid} value={pid}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {showOverview && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <PersonIcon className="mr-2 text-ink-500" size={20} />
                  <h3 className="text-lg font-semibold">{t('members.overview', 'Overview')}</h3>
                </div>
                <Separator className="mb-6" />
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t('members.lineOfBusiness', 'Line of Business')}
                    </div>
                    <div className="text-sm font-semibold text-blue-700 dark:text-blue-300">{memberPlan.lob_name || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('members.plan', 'Plan / Product')}</div>
                    <div className="text-sm">{memberPlan.product_name || '-'}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {/* Data Validation and System Information moved behind Member setup (TRUS-762). */}
          {renderConfiguredFields()}
          {hasMedicaidEnrollment && (
            <EnrollmentStatusWidget
              key={enrollmentKey}
              memberId={member.id}
              onStartEnrollment={handleStartEnrollment}
              onViewEnrollment={handleViewEnrollment}
            />
          )}
          {member && <MemberFieldGroupsSection memberId={member.id} />}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6">
        {/* Edit / Delete live in the page header (member-level actions). */}

        {/* Primary Coordinator Assignment — view-only users cannot reassign. */}
        {memberId && !isViewOnly && !caseManagerViaProgram && (
          <CoordinatorSelector
            memberId={memberId}
            currentCoordinatorId={(member as any).primary_coordinator_id}
            onChanged={(newId) => {
              if (member) (member as any).primary_coordinator_id = newId;
            }}
          />
        )}
        {memberId && (
          <ProgramCaseAssignments memberId={memberId} readOnly={isViewOnly} onLoaded={setCaseManagerViaProgram} />
        )}

        {/* Basic Information — hidden when admin has hidden every field in this card */}
        {!(
          isCoreFieldHidden('member_number') &&
          isCoreFieldHidden('first_name') &&
          isCoreFieldHidden('date_of_birth') &&
          isCoreFieldHidden('gender') &&
          isCoreFieldHidden('preferred_language') &&
          isCoreFieldHidden('race') &&
          isCoreFieldHidden('ethnicity')
        ) && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <PersonIcon className="mr-2 text-ink-500" size={20} />
              <h3 className="text-lg font-semibold">{t('members.basicInformation', 'Basic Information')}</h3>
            </div>
            <Separator className="mb-6" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {!isCoreFieldHidden('member_number') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.memberNumber', 'Member Number')}
                  </div>
                  <div className="text-sm">{member.member_number || '-'}</div>
                </div>
              )}
              {!isCoreFieldHidden('first_name') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.fullName', 'Full Name')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={`${member.first_name} ${member.last_name}`}
                      fieldName="first_name"
                      isShielded={isFieldInShield(member,'first_name')}
                    />
                  </div>
                </div>
              )}
              {!isCoreFieldHidden('date_of_birth') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.dateOfBirth', 'Date of Birth')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={formatDateOnly(member.date_of_birth || member.dob)}
                      fieldName="dob"
                      isShielded={isFieldInShield(member,'dob')}
                    />
                  </div>
                </div>
              )}
              {!isCoreFieldHidden('gender') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.gender', 'Gender')}
                  </div>
                  <div className="text-sm">{member.gender || '-'}</div>
                </div>
              )}
              {!isCoreFieldHidden('preferred_language') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.preferredLanguage', 'Preferred Language')}
                  </div>
                  <div className="text-sm">{member.preferred_language || '-'}</div>
                </div>
              )}
              {!isCoreFieldHidden('race') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.race', 'Race')}
                  </div>
                  <div className="text-sm">{member.race || '-'}</div>
                </div>
              )}
              {!isCoreFieldHidden('ethnicity') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.ethnicity', 'Ethnicity')}
                  </div>
                  <div className="text-sm">{member.ethnicity || '-'}</div>
                </div>
              )}
              {member.data_quality_score !== undefined && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.dataQualityScore', 'Data Quality Score')}
                  </div>
                  <Badge
                    variant={getQualityScoreVariant(member.data_quality_score)}
                    size="sm"
                  >
                    {member.data_quality_score}%
                  </Badge>
                </div>
              )}
              {/* LOB / Plan — pulled from member_plan_enrollments. Hidden when
                  the member isn't enrolled in any canonical health-plan product. */}
              {memberPlan && (memberPlan.lob_name || memberPlan.product_name) && (
                <>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t('members.lineOfBusiness', 'Line of Business')}
                    </div>
                    <div className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                      {memberPlan.lob_name || '-'}
                      {memberPlan.lob_type && (
                        <span className="ml-1 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-mono uppercase text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                          {memberPlan.lob_type.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t('members.plan', 'Plan / Product')}
                    </div>
                    <div className="text-sm">
                      {memberPlan.product_name || '-'}
                      {memberPlan.product_code && (
                        <span className="ml-1 font-mono text-[10px] text-gray-500">
                          {memberPlan.product_code}
                        </span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Contact Information — hidden when admin has hidden every field in this card */}
        {!(isCoreFieldHidden('email') && isCoreFieldHidden('phone_no')) && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <PhoneIcon className="mr-2 text-ink-500" size={20} />
              <h3 className="text-lg font-semibold">{t('members.contactInformation', 'Contact Information')}</h3>
            </div>
            <Separator className="mb-6" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {!isCoreFieldHidden('email') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('common.email', 'Email')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={member.email}
                      fieldName="email"
                      isShielded={isFieldInShield(member,'email')}
                    />
                  </div>
                </div>
              )}
              {!isCoreFieldHidden('phone_no') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('common.phone', 'Phone')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={member.phone_number || member.phone_no}
                      fieldName="phone_no"
                      isShielded={isFieldInShield(member,'phone_no')}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Address — hidden when admin has hidden every field in this card */}
        {!(
          isCoreFieldHidden('street_address') &&
          isCoreFieldHidden('city') &&
          isCoreFieldHidden('state') &&
          isCoreFieldHidden('zip_code') &&
          isCoreFieldHidden('country')
        ) && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <HomeIcon className="mr-2 text-ink-500" size={20} />
              <h3 className="text-lg font-semibold">{t('common.address', 'Address')}</h3>
            </div>
            <Separator className="mb-6" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6">
              {!isCoreFieldHidden('street_address') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.streetAddress', 'Street Address')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={member.street_address}
                      fieldName="street_address"
                      isShielded={isFieldInShield(member,'street_address')}
                    />
                  </div>
                </div>
              )}
              {!isCoreFieldHidden('city') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('common.city', 'City')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={member.city}
                      fieldName="city"
                      isShielded={isFieldInShield(member,'city')}
                    />
                  </div>
                </div>
              )}
              {!isCoreFieldHidden('state') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('common.state', 'State')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={member.state}
                      fieldName="state"
                      isShielded={isFieldInShield(member,'state')}
                    />
                  </div>
                </div>
              )}
              {!isCoreFieldHidden('zip_code') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('common.zip', 'Zip Code')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={member.zip_code}
                      fieldName="zip_code"
                      isShielded={isFieldInShield(member,'zip_code')}
                    />
                  </div>
                </div>
              )}
              {!isCoreFieldHidden('country') && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('common.country', 'Country')}
                  </div>
                  <div className="text-sm">{member.country || '-'}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}


        {/* Insurance Information — hidden when admin has hidden every field in this card */}
        {!(
          isCoreFieldHidden('medicaid_id') &&
          isCoreFieldHidden('medicare_id') &&
          isCoreFieldHidden('commercial_insurance_id') &&
          isCoreFieldHidden('patient_no')
        ) && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center mb-4">
              <LocalHospitalIcon className="mr-2 text-ink-500" size={20} />
              <h3 className="text-lg font-semibold">{t('members.insuranceInformation', 'Insurance Information')}</h3>
            </div>
            <Separator className="mb-6" />
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {!isCoreFieldHidden('medicaid_id') && (
                <>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t('members.medicaidId', 'Medicaid ID')}
                    </div>
                    <div className="text-sm">
                      <ShieldedField
                        value={member.medicaid_id}
                        fieldName="medicaid_id"
                        isShielded={isFieldInShield(member, 'medicaid_id')}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      {t('members.cin', 'CIN')}
                    </div>
                    <div className="text-sm">
                      <ShieldedField
                        value={member.medicaid_id}
                        fieldName="medicaid_id"
                        isShielded={isFieldInShield(member, 'medicaid_id')}
                      />
                    </div>
                  </div>
                </>
              )}
              {!isCoreFieldHidden('medicare_id') && member.medicare_id && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.medicareId', 'Medicare ID')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={member.medicare_id}
                      fieldName="medicare_id"
                      isShielded={isFieldInShield(member, 'medicare_id')}
                    />
                  </div>
                </div>
              )}
              {!isCoreFieldHidden('commercial_insurance_id') && member.commercial_insurance_id && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.commercialInsuranceId', 'Commercial Insurance ID')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={member.commercial_insurance_id}
                      fieldName="commercial_insurance_id"
                      isShielded={isFieldInShield(member, 'commercial_insurance_id')}
                    />
                  </div>
                </div>
              )}
              {!isCoreFieldHidden('patient_no') && member.patient_no && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('members.patientNumber', 'Patient Number')}
                  </div>
                  <div className="text-sm">
                    <ShieldedField
                      value={member.patient_no}
                      fieldName="patient_no"
                      isShielded={isFieldInShield(member, 'patient_no')}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Medicaid Enrollment Status Widget - Only show when org has medicaid enrollment capability */}
        {hasMedicaidEnrollment && (
          <EnrollmentStatusWidget
            key={enrollmentKey}
            memberId={member.id}
            onStartEnrollment={handleStartEnrollment}
            onViewEnrollment={handleViewEnrollment}
          />
        )}


        {/* Custom Fields */}
        {renderCustomFields()}

        {/* Repeatable field groups configured for this organization */}
        {member && <MemberFieldGroupsSection memberId={member.id} />}
      </div>
    );
  };

  // Inline custom field save — sends ONLY the changed field, avoids 502 timeout
  const saveCustomFieldInline = async (fieldName: string, newValue: string) => {
    if (!member) return false;
    try {
      const baseUrl = (productConfig.weaveinApi.baseUrl ?? '').replace(/\/$/, '');
      const token = localStorage.getItem('auth_token') || '';
      const cboId = localStorage.getItem('active_cbo_id') || '';
      const resp = await fetch(`${baseUrl}/api/members/${member.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId },
        body: JSON.stringify({ custom_fields: [{ field_name: fieldName, value: newValue }] }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // Update local member state with a NEW object so React re-renders — this
      // makes conditional visibility (visibility_rules) re-evaluate live when a
      // parent field changes (e.g. selecting "On Island" reveals its sub-field).
      setMember((prev) =>
        prev ? { ...prev, custom_fields: { ...(prev.custom_fields || {}), [fieldName]: newValue } } : prev,
      );
      return true;
    } catch (e: any) {
      console.error('Failed to save custom field:', e);
      alert(t('members.failedToSaveX', 'Failed to save: {{error}}', { error: e.message }));
      return false;
    }
  };

  const renderCustomFields = () => {
    if (!member) return null;

    const customFieldValues = member.custom_fields || {};

    const categorizedFields = new Map<string, CustomField[]>();
    const uncategorizedFields: CustomField[] = [];

    fields.forEach((field) => {
      if (field.hidden) return;
      // Honor conditional visibility_rules (same evaluator the forms use).
      if (field.visibility_rules && !isFieldVisible(
        { field_id: field.name, visibility_rules: field.visibility_rules as any },
        [],
        customFieldValues,
      )) return;
      if (field.category) {
        if (!categorizedFields.has(field.category)) categorizedFields.set(field.category, []);
        categorizedFields.get(field.category)!.push(field);
      } else {
        uncategorizedFields.push(field);
      }
    });

    if (categorizedFields.size === 0 && uncategorizedFields.length === 0) {
      return (
        <Card>
          <CardContent className="p-6 text-center text-gray-400">
            <p>{t('members.noCustomFieldsConfigured', 'No custom fields configured for this organization.')}</p>
            <p className="text-xs mt-1">{t('members.goToAdminMemberFields', 'Go to Admin → Member Fields to create custom fields.')}</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <>
        {Array.from(categorizedFields.entries()).map(([category, categoryFields]) => (
          <Card key={category}>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-1">{category}</h3>
              <p className="text-xs text-gray-400 mb-4">{t('members.fieldsWithValues', '{{count}} field(s) — {{filled}} with values', { count: categoryFields.length, filled: categoryFields.filter(f => customFieldValues[f.name]).length })}</p>
              <Separator className="mb-4" />
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {categoryFields.map(f => <InlineCustomFieldEditor key={f.id} field={f} value={customFieldValues[f.name]} memberId={member.id} onSave={saveCustomFieldInline} recordValues={customFieldValues} />)}
              </div>
            </CardContent>
          </Card>
        ))}

        {uncategorizedFields.length > 0 && (
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-1">{t('members.additionalInformation', 'Additional Information')}</h3>
              <p className="text-xs text-gray-400 mb-4">{t('members.fieldsCount', '{{count}} field(s)', { count: uncategorizedFields.length })}</p>
              <Separator className="mb-4" />
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {uncategorizedFields.map(f => <InlineCustomFieldEditor key={f.id} field={f} value={customFieldValues[f.name]} memberId={member.id} onSave={saveCustomFieldInline} recordValues={customFieldValues} />)}
              </div>
            </CardContent>
          </Card>
        )}
      </>
    );
  };

  const renderBilling = () => {
    if (loadingBilling) {
      return (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      );
    }

    const hasClaims = !!billingSummary && billingSummary.claims.length > 0;
    if (!hasClaims && billingPending.length === 0) {
      return (
        <Alert className="mt-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            {t('members.noBilling', 'No claims or billing activity found for this member')}
          </AlertDescription>
        </Alert>
      );
    }

    const usd = (n: number) =>
      new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

    // "Documented services ready to bill" — completing one auto-creates its claim.
    const soapNote = billingDraftNote;
    const pendingSection =
      billingPending.length > 0 ? (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
              {t('billing.readyToBill', 'Documented services ready to bill')}
            </span>
            <Button size="sm" variant="outline" disabled={billingDrafting} onClick={draftProgressNote}>
              {billingDrafting
                ? t('billing.drafting', 'Drafting…')
                : t('billing.draftNote', 'Draft progress note (AI)')}
            </Button>
          </div>

          {soapNote && (
            <div className="mb-3 rounded-md border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-900 p-3 text-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                {t('billing.aiProgressNote', 'AI-drafted progress note (from telehealth transcript)')}
              </div>
              {(['subjective', 'objective', 'assessment', 'plan'] as const).map((k) =>
                soapNote[k] ? (
                  <div key={k} className="mb-1">
                    <span className="font-medium capitalize text-gray-900 dark:text-gray-100">{k}: </span>
                    <span className="text-gray-700 dark:text-gray-300">{soapNote[k]}</span>
                  </div>
                ) : null
              )}
              <div className="mt-1 text-xs text-gray-400">
                {t('billing.reviewSignHint', 'Clinician reviews & signs, then completes the activity to bill.')}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {billingPending.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-800 dark:text-gray-200">{a.activity_name}</span>
                <Button
                  size="sm"
                  disabled={billingBusy === a.id}
                  onClick={() => completeAndBill(a.enrollment_id, a.id)}
                >
                  {billingBusy === a.id
                    ? t('billing.billing', 'Billing…')
                    : t('billing.completeAndBill', 'Complete & bill')}
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-blue-700 dark:text-blue-300">
            {t(
              'billing.completeAndBillHint',
              'Completing marks the activity done and auto-creates the claim (mock clearinghouse 837 → 277 accepted).'
            )}
          </div>
        </div>
      ) : null;

    if (!hasClaims) {
      return <div className="mt-2 space-y-6">{pendingSection}</div>;
    }

    const claimStatusVariant = (status: string | null): 'success' | 'warning' | 'danger' | 'info' | 'default' => {
      switch ((status || '').toLowerCase()) {
        case 'paid':
          return 'success';
        case 'partial_paid':
        case 'submitted':
        case 'accepted':
          return 'warning';
        case 'denied':
          return 'danger';
        case 'draft':
          return 'info';
        default:
          return 'default';
      }
    };

    const verdictVariant = (v: string | null): 'success' | 'warning' | 'danger' | 'default' => {
      switch ((v || '').toLowerCase()) {
        case 'allow':
          return 'success';
        case 'warn':
          return 'warning';
        case 'block':
          return 'danger';
        default:
          return 'default';
      }
    };

    if (!billingSummary) return <div className="mt-2 space-y-6">{pendingSection}</div>;
    const totals = billingSummary.totals;

    return (
      <div className="mt-2 space-y-6">
        <div className="flex items-center justify-end">
          <Button size="sm" variant="outline" disabled={billingResetting} onClick={resetBillingDemo}>
            {billingResetting ? t('billing.resetting', 'Resetting…') : t('billing.resetDemo', 'Reset demo')}
          </Button>
        </div>
        {pendingSection}
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: t('billing.billed', 'Billed'), value: usd(totals.billed) },
            { label: t('billing.reimbursed', 'Reimbursed'), value: usd(totals.paid) },
            { label: t('billing.denied', 'Denied'), value: usd(totals.denied_amount) },
            { label: t('billing.recovered', 'Recovered on appeal'), value: usd(totals.recovered_amount) },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{s.label}</div>
                <div className="text-xl font-semibold mt-1">{s.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {t('billing.claimCount', '{{count}} claim(s)', { count: totals.claim_count })}
          {' · '}
          {t('billing.paidClaims', '{{paid}} paid', { paid: totals.paid_claims })}
        </div>

        {/* Per-claim detail */}
        {billingSummary.claims.map((claim) => {
          const denial = claim.denials[0];
          return (
            <Card key={claim.id}>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">{claim.claim_number}</CardTitle>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {claim.program_name || t('billing.program', 'Program')}
                      {' · '}
                      {claim.payer_name || t('billing.payer', 'Payer')}
                      {' · '}
                      {fmt.short(claim.service_date_from)}
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={claimStatusVariant(claim.claim_status)} size="sm">
                      {(claim.claim_status || '').replace('_', ' ')}
                    </Badge>
                    <div className="text-sm mt-1">
                      <span className="font-semibold">{usd(claim.total_paid)}</span>
                      <span className="text-gray-400"> / {usd(claim.total_charge)}</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('billing.code', 'Code')}</TableHead>
                        <TableHead>{t('billing.service', 'Service')}</TableHead>
                        <TableHead className="text-right">{t('billing.units', 'Units')}</TableHead>
                        <TableHead className="text-right">{t('billing.charge', 'Charge')}</TableHead>
                        <TableHead className="text-right">{t('billing.paid', 'Paid')}</TableHead>
                        <TableHead>{t('billing.lineStatus', 'Status')}</TableHead>
                        <TableHead>{t('billing.aiVerdict', 'Billing AI')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {claim.line_items.map((li) => (
                        <TableRow key={`${claim.id}-${li.line_number}`}>
                          <TableCell className="font-mono text-xs">
                            {li.hcpcs_code || li.cpt_code || '-'}
                            {li.modifier_1 ? `-${li.modifier_1}` : ''}
                          </TableCell>
                          <TableCell>{li.service_description || '-'}</TableCell>
                          <TableCell className="text-right">{li.units}</TableCell>
                          <TableCell className="text-right">{usd(li.charge_amount)}</TableCell>
                          <TableCell className="text-right">{usd(li.paid_amount)}</TableCell>
                          <TableCell>
                            <Badge variant={claimStatusVariant(li.line_status)} size="sm">
                              {li.line_status || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {li.ai_verdict ? (
                              <Badge variant={verdictVariant(li.ai_verdict)} size="sm">
                                {li.ai_verdict} · {(li.ai_denial_probability * 100).toFixed(0)}%
                              </Badge>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Denial + appeal recovery */}
                {denial && (
                  <div className="mt-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
                    <div className="font-medium text-amber-900 dark:text-amber-200">
                      {t('billing.denialTitle', 'Denial')} · {denial.group_code}-{denial.denial_reason_code} · {usd(denial.denied_amount)}
                    </div>
                    <div className="text-amber-800 dark:text-amber-300 mt-0.5">
                      {denial.denial_reason_description}
                    </div>
                    {denial.appeal_number && (
                      <div className="text-amber-800 dark:text-amber-300 mt-1">
                        {t('billing.appeal', 'Appeal')} {denial.appeal_number}: {denial.appeal_status}
                        {denial.decision_outcome ? ` → ${denial.decision_outcome}` : ''}
                        {denial.approved_amount ? ` (${usd(denial.approved_amount)} ${t('billing.recoveredWord', 'recovered')})` : ''}
                      </div>
                    )}
                  </div>
                )}

                {/* MCP remittance payments */}
                {claim.payments.length > 0 && (
                  <div className="mt-3 text-sm">
                    <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                      {t('billing.remittances', 'MCP Remittances (835)')}
                    </div>
                    {claim.payments.map((pay, idx) => (
                      <div key={idx} className="flex items-center justify-between py-0.5">
                        <span className="text-gray-600 dark:text-gray-300">
                          {pay.era_control_number} · {(pay.payment_method || '').toUpperCase()} {pay.check_eft_number}
                          {pay.check_eft_date ? ` · ${fmt.short(pay.check_eft_date)}` : ''}
                        </span>
                        <span className="font-medium text-green-700 dark:text-green-400">{usd(pay.paid_amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Actions: ledger + receive remittance */}
                <div className="mt-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-2">
                  <button
                    type="button"
                    onClick={() => setBillingLedgerOpen(billingLedgerOpen === claim.id ? null : claim.id)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    {billingLedgerOpen === claim.id
                      ? t('billing.hideLedger', 'Hide ledger')
                      : t('billing.viewLedger', 'View ledger')}
                  </button>
                  {(claim.claim_status || '') !== 'paid' && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={billingBusy === claim.id}
                      onClick={async () => {
                        setBillingBusy(claim.id);
                        try {
                          await claimsApi.postMockRemittance(claim.id);
                          if (memberId) await loadBilling(memberId);
                        } catch (e) {
                          console.error('mock remittance failed', e);
                        } finally {
                          setBillingBusy(null);
                        }
                      }}
                    >
                      {billingBusy === claim.id
                        ? t('billing.posting', 'Posting…')
                        : t('billing.receiveRemittance', 'Receive remittance (mock)')}
                    </Button>
                  )}
                </div>
                {billingLedgerOpen === claim.id && (
                  <div className="mt-3">
                    <ClaimLedgerView claimId={claim.id} />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  const renderReferrals = () => {
    if (loadingReferrals) {
      return (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      );
    }

    if (referrals.length === 0) {
      return (
        <Alert className="mt-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            {t('referrals.noReferralsForMember', 'No referrals found for this member')}
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('referrals.referralNumber', 'Referral #')}</TableHead>
              <TableHead>{t('common.created', 'Created')}</TableHead>
              <TableHead>{t('common.status', 'Status')}</TableHead>
              <TableHead>{t('referrals.referredTo', 'Referred to')}</TableHead>
              <TableHead>{t('referrals.primaryDiagnosis', 'Primary Diagnosis')}</TableHead>
              <TableHead>{t('referrals.urgency', 'Urgency')}</TableHead>
              <TableHead>{t('referrals.healthPlan', 'Health Plan')}</TableHead>
              <TableHead>{t('referrals.needs', 'Needs')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {referrals.map((referral: any) => (
              <TableRow key={referral.id}>
                <TableCell>{referral.referralNumber || referral.referral_number || '-'}</TableCell>
                <TableCell>{fmt.short(referral.createdAt || referral.created_at)}</TableCell>
                <TableCell>
                  <Badge
                    variant={getStatusVariant(referral.status)}
                    size="sm"
                  >
                    {referral.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  {referral.referred_to || '-'}
                  {referral.acceptance_status && (
                    <span className="block text-xs text-gray-500">{referral.acceptance_status}</span>
                  )}
                </TableCell>
                {/* Service referrals have no diagnosis; show what the referral is for. */}
                <TableCell>{referral.primaryDiagnosis || referral.primary_diagnosis || referral.service_type_name || '-'}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      referral.urgencyLevel === 'Immediate' || referral.urgency_level === 'Immediate' ? 'danger' :
                      referral.urgencyLevel === 'Within 24h' || referral.urgency_level === 'Within 24h' ? 'warning' :
                      'default'
                    }
                    size="sm"
                  >
                    {referral.urgencyLevel || referral.urgency_level || '-'}
                  </Badge>
                </TableCell>
                <TableCell>{referral.healthPlan || referral.health_plan || '-'}</TableCell>
                <TableCell>
                  {referral.sdohNeeds && referral.sdohNeeds.length > 0
                    ? referral.sdohNeeds.join(', ')
                    : referral.sdoh_needs && referral.sdoh_needs.length > 0
                    ? referral.sdoh_needs.join(', ')
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const goToCreateEnrollment = () => {
    if (memberId) {
      localStorage.setItem('pendingEnrollmentMemberId', String(memberId));
    }
    onNavigate?.('enrollment-new');
  };

  const renderConflicts = () => {
    if (loadingConflicts) {
      return (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      );
    }

    if (conflicts.length === 0) {
      return (
        <Alert className="mt-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <AlertDescription className="text-green-800 dark:text-green-200">
            {t('members.noDataConflicts', 'No data conflicts found for this member')}
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <div className="mt-4">
        <Alert className="mb-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            {t('members.dataConflictsExplanation', 'Data conflicts occur when member information differs between the global profile and organization-specific records. The AI system automatically flags these for review to ensure data accuracy and consistency across the platform.')}
          </AlertDescription>
        </Alert>
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('members.fieldName', 'Field Name')}</TableHead>
                <TableHead>{t('members.globalValue', 'Global Value')}</TableHead>
                <TableHead>{t('members.organizationValue', 'Organization Value')}</TableHead>
                <TableHead>{t('members.confidenceScore', 'Confidence Score')}</TableHead>
                <TableHead>{t('common.status', 'Status')}</TableHead>
                <TableHead>{t('members.detected', 'Detected')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {conflicts.map((conflict) => (
                <TableRow key={conflict.id}>
                  <TableCell>
                    <div className="text-sm font-medium">
                      {conflict.field_name}
                    </div>
                  </TableCell>
                  <TableCell>{conflict.global_value || '-'}</TableCell>
                  <TableCell>{conflict.org_value || '-'}</TableCell>
                  <TableCell>
                    <Badge
                      variant={getQualityScoreVariant(conflict.confidence_score)}
                      size="sm"
                    >
                      {conflict.confidence_score}%
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={conflict.resolved ? 'success' : 'warning'}
                      size="sm"
                    >
                      {conflict.resolved ? t('members.resolved', 'Resolved') : t('members.pending', 'Pending')}
                    </Badge>
                  </TableCell>
                  <TableCell>{fmt.short(conflict.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  if (loading && !isCreateMode) {
    return (
      <div className="flex justify-center items-center min-h-[400px] flex-col gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {t('members.loadingProfile', 'Loading member profile...')}
        </div>
      </div>
    );
  }

  if (!isCreateMode && (error || !member)) {
    return (
      <div className="p-6">
        <Alert className="mb-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <AlertDescription className="text-red-800 dark:text-red-200">
            {error || t('members.memberNotFound', 'Member not found')}
          </AlertDescription>
        </Alert>
        <Button
          variant="primary"
          onClick={() => onNavigate?.('members')}
        >
          {t('members.backToMembers', 'Back to Members')}
        </Button>
      </div>
    );
  }

  // ---- Navigation (TRUS-528 tree, TRUS-762 placement) ------------------------
  const unresolvedConflicts = conflicts.filter(c => !c.resolved).length;

  // The two things the SERVER cannot decide, keyed by tab_key: gates that
  // depend on the loaded member, and badges. Everything else — which tabs
  // exist, what they are called, what order they are in, whether the org
  // switched them off, and (TRUS-762) whether they sit on the rail or behind
  // Member setup — comes from /profile-layout. A tab absent here has no client
  // gate and no badge.
  const programsBadgeCount = programsCount ?? enrollments.length;
  const tabExtras: Record<
    string,
    { gate?: boolean; badge?: { count: number; variant: 'primary' | 'danger' } }
  > = {
    // Programs carries what used to be the Enrollments count.
    programs: {
      badge: programsBadgeCount > 0 ? { count: programsBadgeCount, variant: 'primary' } : undefined,
    },
    billing: {
      // The capability decides whether the org has billing at all; the
      // show_billing setting is the org's "hide it anyway" override and is
      // already folded into `visible` by the server.
      gate: isBillingSectionVisible(orgHasBilling, viewSettings?.show_billing),
      badge: billingSummary && billingSummary.totals.claim_count > 0
        ? { count: billingSummary.totals.claim_count, variant: 'primary' }
        : undefined,
    },
    'care-coordination': {
      badge: referrals.length > 0 ? { count: referrals.length, variant: 'primary' } : undefined,
    },
    // Referrals made OUTSIDE the platform (TRUS-511) — deliberately its own
    // tab: TRUS-519 turns the clinical one off for the very org that asked for
    // this, so the two must be switchable independently.
    'external-referrals': { gate: orgHasExternalReferrals },
    'health-plan': { gate: !!member?.commercial_insurance_id },
    history: {
      badge: unresolvedConflicts > 0 ? { count: unresolvedConflicts, variant: 'danger' } : undefined,
    },
    'work-compliance': { gate: hasMedicaidEnrollment },
    workforce: { gate: showWorkforceTab },
    // A member who donates is a donor too; the tab used to exist only on
    // contact records, so a program member's gifts were invisible here.
    giving: { gate: isDonorRecord },
    'data-validation': { gate: !!member },
    'system-information': { gate: !!member },
  };

  const labelFor = (el: LayoutLabelSource) => resolveLayoutLabel(el, t);
  const layoutSections = normalizeProfileSections(profileLayout?.sections ?? FALLBACK_PROFILE_SECTIONS);

  /** The tab's own gates: the org's settings (server `visible`), then the client's. */
  const passesGates = (tab: ProfileLayoutTab): boolean => {
    if (!tab.visible) return false;
    // An org's own note-category tabs (TRUS-420) carry one gate the server does
    // not apply: Recovery Plan additionally needs the recovery_plan capability.
    // `sectionCategories` is the set that already passed that filter.
    if (tab.note_category_id !== undefined) {
      return sectionCategories.some(c => c.id === tab.note_category_id);
    }
    const extra = tabExtras[tab.tab_key];
    return extra?.gate === undefined ? true : extra.gate;
  };

  // Engagement: one entry whose segments each keep their own gate. No visible
  // segment, no entry.
  const engagementSegments = layoutSections
    .flatMap(section => section.tabs)
    .filter(tab => tabMeta(tab).segmentOf === 'engagement' && passesGates(tab))
    .map(tab => ({ id: tab.tab_key, label: labelFor(tab) }));
  const activeSegment =
    engagementSegments.find(seg => seg.id === engagementSegment)?.id ?? engagementSegments[0]?.id ?? null;

  /** Listed somewhere (rail or setup) and allowed by its gates. */
  const isListed = (tab: ProfileLayoutTab): boolean => {
    const meta = tabMeta(tab);
    if (meta.aliasOf || meta.segmentOf) return false;
    if (tab.tab_key === 'engagement') return tab.visible && engagementSegments.length > 0;
    return passesGates(tab);
  };

  type NavItem = { id: string; label: string; badge?: { count: number; variant: 'primary' | 'danger' } };
  const toItem = (tab: ProfileLayoutTab): NavItem => ({
    id: tab.tab_key,
    label: labelFor(tab),
    badge: tabExtras[tab.tab_key]?.badge,
  });

  const navSections: Array<{ key: string; heading: string; items: NavItem[] }> = layoutSections
    .map(section => ({
      key: section.section_key,
      heading: labelFor(section),
      items: section.tabs.filter(tab => tabMeta(tab).placement === 'rail' && isListed(tab)).map(toItem),
    }))
    .filter(section => section.items.length > 0);

  /** Member setup: the same gates, in the tree's order. */
  const setupItems: NavItem[] = layoutSections.flatMap(section =>
    section.tabs.filter(tab => tabMeta(tab).placement === 'setup' && isListed(tab)).map(toItem),
  );
  const activeSetupItem = setupItems.find(item => item.id === activeTab) || null;

  /**
   * Forms pinned to the sidebar (TRUS-564; by role TRUS-762). The server has
   * already kept only pins for programs this member is enrolled in and roles
   * this user holds, and resolved the enrollment each one opens against.
   */
  const pinnedFormTabs = layoutSections
    .flatMap(section => section.tabs)
    .filter(tab => tab.pinned_form_id != null && tab.form_template_id != null && isListed(tab));

  return (
    <div className="p-6">
      {/* A report sent the user here to fix something; this is the way back to it, with
          the report's filters intact. Renders nothing when they arrived any other way. */}
      <ReturnToReportBanner />
      {/* An archived (soft-deleted) member. The record is still here in full — the profile
          must SAY so rather than looking live, and offer the way back. Without this, the only
          route out of an accidental archive was a support ticket (TRUS-371). */}
      {!isCreateMode && isArchived && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-700 dark:bg-amber-900/30">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              <strong>{t('members.archivedTitle', 'This member is archived.')}</strong>{' '}
              {t(
                'members.archivedHelp',
                'They are hidden from your member list, but nothing was deleted — all their data is intact. Restoring puts them back.',
              )}
            </div>
            <Button variant="outline" size="sm" onClick={handleRestore} disabled={loading}>
              <RotateCcw className="mr-1.5 h-4 w-4" />
              {t('members.restore', 'Restore member')}
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex justify-between items-start flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-4 mb-2 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <UserCircle className="h-6 w-6 text-ink-500" />
              {isCreateMode ? (
                <span>{t('members.newMember', 'New Member')}</span>
              ) : (
                <>
                  <ShieldedField
                    value={`${member!.first_name} ${member!.last_name}`}
                    fieldName="first_name"
                    isShielded={isFieldInShield(member,'first_name')}
                    showRevealButton={false}
                  />
                  {memberId !== undefined && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleFavorite(memberId)}
                      className={`${INK_GHOST_BUTTON} !p-1`}
                      aria-pressed={isFavorite(memberId)}
                      title={isFavorite(memberId) ? t('members.removeFromFavorites', 'Remove from favorites') : t('members.addToFavorites', 'Add to favorites')}
                      aria-label={isFavorite(memberId) ? t('members.removeFromFavorites', 'Remove from favorites') : t('members.addToFavorites', 'Add to favorites')}
                    >
                      <Star
                        className={`w-5 h-5 transition-colors ${
                          isFavorite(memberId) ? 'text-v2accent fill-v2accent' : 'text-ink-400'
                        }`}
                      />
                    </Button>
                  )}
                  {isViewOnly && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                      title={t('members.viewOnlyTooltip', 'You can view this member but cannot edit. Editing is restricted to the assigned staff.')}
                    >
                      {t('members.viewOnly', 'View only')}
                    </span>
                  )}
                  {previewRole && previewCanEdit != null && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded border border-v2border text-xs font-medium text-ink-500"
                      title={t(
                        'members.previewRoleTooltip',
                        'Previewing this member as the selected role. Edit affordances reflect that role\'s member-profile permission.'
                      )}
                    >
                      {`${t('members.previewingAs', 'Previewing as')} ${previewRoleLabel || previewRole} · ${
                        previewCanEdit ? t('members.canEdit', 'can edit') : t('members.viewOnly', 'View only')
                      }`}
                    </span>
                  )}
                </>
              )}
            </h1>
            {member?._shielded && !isRevealed && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={requestReveal}
                className={`${INK_GHOST_BUTTON} !border !border-v2border`}
              >
                <Eye className="w-3.5 h-3.5" />
                {t('members.revealAll', 'Reveal All')}
              </Button>
            )}
            {/* Phase 2: Profile header fields (CCL, etc.) */}
            {member && fields.filter((f: any) => f.display_location === 'profile_header' && member.custom_fields?.[f.name]).map((f: any) => (
              <span key={f.id} className="inline-flex items-center px-2 py-0.5 rounded border border-v2border text-xs font-medium text-ink-500">
                {f.friendly_name}: {member.custom_fields[f.name]}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* TRUS-636: printing is reading, so view-only users get it too. */}
          {!isCreateMode && member && (
            <Button variant="outline" icon={<PrinterIcon size={16} />} onClick={() => setPrintOpen(true)}>
              {t('printPacket.button', 'Print')}
            </Button>
          )}
          {!isCreateMode && member && !isViewOnly && (
            <>
              {/* TRUS-762: Edit edits the Member Profile (demographics) only, so
                  it shows only there. Every other tab has its own edit path
                  (Programs rows, section edit, Activities screens, ...). The
                  key is the resolved one: no alias resolves to demographics. */}
              <MemberProfileEditButton
                activeTab={activeTab}
                onEdit={() => onNavigate?.('members-edit', member.id)}
              />
              {/* Quiet until hovered; handleDelete confirms before archiving. */}
              <Button
                variant="ghost"
                onClick={handleDelete}
                size="icon"
                className={DANGER_GHOST_BUTTON}
                title={t('members.deleteMember', 'Delete member')}
                aria-label={t('members.deleteMember', 'Delete member')}
              >
                <DeleteIcon size={16} />
              </Button>
            </>
          )}
          <Button
            variant="outline"
            icon={<ArrowLeftIcon size={16} />}
            onClick={() => onNavigate?.('members')}
          >
            {t('members.backToList', 'Back to List')}
          </Button>
          {/* TRUS-762: Member setup — record upkeep (consent, notifications,
              family, data enrichment, ...) behind one gear. Each entry keeps its
              own gate; with none visible there is no gear. */}
          {!isCreateMode && member && setupItems.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSetupOpen(true)}
              className={`${INK_GHOST_BUTTON} ${activeSetupItem ? '!bg-paper-2 !text-ink' : ''}`}
              title={t('members.memberSetup', 'Member setup')}
              aria-label={t('members.memberSetup', 'Member setup')}
              aria-haspopup="dialog"
            >
              <SettingsIcon size={18} />
            </Button>
          )}
        </div>
      </div>
      <Sheet open={setupOpen} onOpenChange={setSetupOpen}>
        <SheetContent side="right" className="!bg-surface !border-v2border">
          <SheetHeader>
            <SheetTitle className="text-ink">{t('members.memberSetup', 'Member setup')}</SheetTitle>
            <SheetDescription className="text-ink-500">
              {t('members.memberSetupHelp', 'Set up once, open when needed.')}
            </SheetDescription>
          </SheetHeader>
          <ul className="px-4 pb-4 space-y-0.5">
            {setupItems.map(item => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    openTab(item.id);
                    setSetupOpen(false);
                  }}
                  aria-current={activeTab === item.id ? 'page' : undefined}
                  className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab === item.id ? 'bg-paper-2 text-ink' : 'text-ink hover:bg-paper-2'
                  }`}
                >
                  <span>{item.label}</span>
                  {item.badge && (
                    <Badge variant={item.badge.variant} size="sm">
                      {item.badge.count}
                    </Badge>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </SheetContent>
      </Sheet>
      {printOpen && member && (
        <PrintPacketDialog open={printOpen} onClose={() => setPrintOpen(false)} memberId={member.id} />
      )}

      {/*
        Sidebar-grouped navigation. Sections organize the previously-flat
        tab strip; the existing <TabsContent> blocks below render based on
        the activeTab state set by these buttons.
      */}
      <Tabs defaultValue="demographics" value={activeTab} onValueChange={openTab} className="mb-6">
        {(() => {

          return (
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Sidebar nav */}
              <aside className={`${navRailCollapsed ? 'lg:w-10' : 'lg:w-56'} lg:shrink-0 transition-[width] duration-150`}>
                {/* Mobile: horizontal scroll fallback */}
                <nav className="lg:hidden overflow-x-auto -mx-1 pb-2">
                  <div className="flex gap-1 px-1 w-max">
                    {navSections.flatMap(s => s.items).map(item => {
                      const isActive = activeTab === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => openTab(item.id)}
                          className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                          }`}
                        >
                          <span className="flex items-center gap-1.5">
                            {item.label}
                            {item.badge && (
                              <Badge variant={item.badge.variant} size="sm">
                                {item.badge.count}
                              </Badge>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </nav>

                {/* Desktop: collapsed rail — a single button to reopen the nav. */}
                {navRailCollapsed && (
                  <div className="hidden lg:flex sticky top-4 justify-center">
                    <button
                      type="button"
                      onClick={toggleNavRail}
                      className="p-2 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800"
                      title={t('members.expandNav', 'Expand navigation')}
                      aria-label={t('members.expandNav', 'Expand navigation')}
                    >
                      <ChevronsRightIcon size={16} />
                    </button>
                  </div>
                )}

                {/* Desktop: grouped vertical sidebar */}
                <nav className={`${navRailCollapsed ? 'hidden' : 'hidden lg:block'} sticky top-4 space-y-5`}>
                  <div className="flex justify-end px-1">
                    <button
                      type="button"
                      onClick={toggleNavRail}
                      className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                      title={t('members.collapseNav', 'Collapse navigation')}
                      aria-label={t('members.collapseNav', 'Collapse navigation')}
                    >
                      <ChevronsLeftIcon size={16} />
                    </button>
                  </div>
                  {navSections.map(section => {
                    // Keyed on the stable section_key, never the heading: an
                    // org renaming a section must not silently re-expand it
                    // (and two orgs' persisted state must not collide).
                    const sectionCollapsed = collapsedNavSections.has(section.key);
                    // Honor the user's collapse toggle directly. The active tab's
                    // content still renders in the main area even when its nav
                    // button is hidden, so collapsing never strands the user.
                    const showItems = !sectionCollapsed;
                    return (
                    <div key={section.key}>
                      <button
                        type="button"
                        onClick={() => toggleNavSection(section.key)}
                        className="w-full flex items-center gap-1 px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        aria-expanded={showItems}
                      >
                        {sectionCollapsed
                          ? <ChevronRightIcon size={14} className="shrink-0" />
                          : <ExpandMoreIcon size={14} className="shrink-0" />}
                        <span>{section.heading}</span>
                      </button>
                      {showItems && (
                      <ul className="space-y-0.5">
                        {section.items.map(item => {
                          const isActive = activeTab === item.id;
                          return (
                            <li key={item.id}>
                              <button
                                type="button"
                                onClick={() => openTab(item.id)}
                                className={`group w-full flex items-center justify-between rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                                  isActive
                                    ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                                }`}
                              >
                                <span>{item.label}</span>
                                {item.badge && (
                                  <Badge variant={item.badge.variant} size="sm">
                                    {item.badge.count}
                                  </Badge>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                      )}
                    </div>
                    );
                  })}
                </nav>
              </aside>

              {/* Content area */}
              <div className="flex-1 min-w-0">
                {/* Which Member setup entry is open, and the way back to the list. */}
                {activeSetupItem && (
                  <div className="mb-4 flex items-center gap-1.5 text-xs text-ink-500">
                    <SettingsIcon size={14} className="shrink-0" />
                    <button type="button" onClick={() => setSetupOpen(true)} className="hover:text-ink hover:underline">
                      {t('members.memberSetup', 'Member setup')}
                    </button>
                    <ChevronRightIcon size={14} className="shrink-0" />
                    <span className="font-medium text-ink">{activeSetupItem.label}</span>
                  </div>
                )}
                {/* Tab Content */}
                <TabsContent value="demographics">
          {isCreateMode ? (
            <AddEditMemberNew
              onNavigate={(tab, newMemberId) => {
                if (newMemberId) {
                  // Member created — transition to full profile in-place
                  setMemberId(newMemberId);
                }
              }}
            />
          ) : (
            <>
              {/* TRUS-729: renders only for members in the 1 Jan 2027 FFS cohort,
                  and only in orgs with the ffs_transition capability (TRUS-739). */}
              {memberId && orgHasFfsTransition && <MemberFfsContinuityPanel memberId={memberId} />}
              {renderCoreInfo()}
            </>
          )}
        </TabsContent>

        {/* In create mode, all non-demographics tabs show a save-first prompt */}
        {isCreateMode && activeTab !== 'demographics' && (
          <CreateModeGate tabName={activeTab.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} onGoBack={() => setActiveTab('demographics')} />
        )}

        {!isCreateMode && (
        <>
        {isBillingSectionVisible(orgHasBilling, viewSettings?.show_billing) && (
        <TabsContent value="billing">
          <div>
            <h2 className="text-xl font-semibold mb-4">
              {t('members.billing', 'Billing & Reimbursement')}
            </h2>
            {renderBilling()}
          </div>
        </TabsContent>
        )}


        {member?.commercial_insurance_id && (
        <TabsContent value="health-plan">
          {memberId && <MemberEnrollmentsTab memberId={memberId} />}
        </TabsContent>
        )}

        <TabsContent value="family">
          {memberId && <FamilyMembersTab memberId={memberId} isReadOnly={isViewOnly} />}
        </TabsContent>

        <TabsContent value="households">
          {memberId && <MemberHouseholdsTab memberId={memberId} onNavigate={onNavigate} />}
        </TabsContent>

        <TabsContent value="caregivers">
          {memberId && <CaregiversTab memberId={memberId} isReadOnly={isViewOnly} />}
        </TabsContent>

        <TabsContent value="external-referrals">
          {memberId && activeTab === 'external-referrals' && (
            <ExternalReferralsPanel memberId={memberId} readOnly={isViewOnly} />
          )}
        </TabsContent>

        <TabsContent value="care-coordination">
          {memberId && activeTab === 'care-coordination' && (
            <div>
              <SubTabBar
                value={careSub}
                onChange={(v) => setCareSub(v as 'referrals' | 'coordination' | 'workflows')}
                tabs={[
                  { id: 'coordination', label: t('members.careCoordination', 'Care Coordination') },
                  { id: 'referrals', label: t('referrals.title', 'Referrals') },
                  ...(viewSettings?.show_workflows !== false
                    ? [{ id: 'workflows', label: t('members.workflows', 'Workflows') }]
                    : []),
                ]}
              />
              {careSub === 'referrals' ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold">{t('members.memberReferrals', 'Member Referrals')}</h2>
                    {!isViewOnly && !showReferralForm && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => setShowNetworkReferral(true)}>
                          <NetworkIcon className="w-4 h-4 mr-1" />
                          {t('members.networkReferral.action', 'Refer to a network org')}
                        </Button>
                        <Button size="sm" onClick={() => setShowReferralForm(true)}>
                          <Plus className="w-4 h-4 mr-1" />
                          {t('members.createReferral', 'Create referral')}
                        </Button>
                      </div>
                    )}
                  </div>
                  {!isViewOnly && (
                    <ReferToNetworkOrgModal
                      open={showNetworkReferral}
                      onOpenChange={setShowNetworkReferral}
                      memberId={memberId}
                      memberName={member ? `${member.first_name || ''} ${member.last_name || ''}`.trim() : undefined}
                    />
                  )}
                  {showReferralForm ? (
                    /* TRUS-757: closed-loop service referral (POST /api/service-referrals),
                       prefilled from the org's member field configuration. Replaces the
                       legacy CreateReferralForm, which wrote the receiver-less referrals table. */
                    <ServiceReferralCreateForm
                      memberId={memberId}
                      onCancel={() => setShowReferralForm(false)}
                      onCreated={() => {
                        if (memberId) loadReferrals(memberId);
                      }}
                      onDone={() => setShowReferralForm(false)}
                    />
                  ) : (
                    renderReferrals()
                  )}
                </div>
              ) : careSub === 'workflows' ? (
                <MemberWorkflowsTab memberId={memberId} />
              ) : (
                <CareCoordinationTab memberId={memberId} showHealthConnect={viewSettings?.show_health_connect !== false} onNavigate={onNavigate} />
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="ai-enrichment">
          {memberId && activeTab === 'ai-enrichment' && (
            <MemberEnrichmentTab
              memberId={memberId}
              memberName={member ? `${member.first_name} ${member.last_name}` : undefined}
            />
          )}
        </TabsContent>

        {/* Benefits — what a payment would do to what she already receives.
            Mounted only while selected: it reads the rules, the intake and the
            period headroom, and none of that is worth fetching for a navigator
            who is on the Demographics tab. */}
        <TabsContent value="benefits">
          {memberId && activeTab === 'benefits' && (
            <MemberBenefitsTab
              memberId={Number(memberId)}
              programId={pickAutoProfileProgramId(enrollments)}
              readOnly={isViewOnly}
            />
          )}
        </TabsContent>

        <TabsContent value="consent">
          {memberId && <MemberConsentPanel memberId={memberId} />}
        </TabsContent>


        <TabsContent value="notifications">
          {memberId && activeTab === 'notifications' && (
            <MemberNotificationsTab
              memberId={memberId}
              memberName={member ? `${member.first_name} ${member.last_name}` : 'Member'}
            />
          )}
        </TabsContent>

        {hasMedicaidEnrollment && (
        <TabsContent value="work-compliance">
          {memberId && <MemberWorkComplianceTab memberId={memberId} />}
        </TabsContent>
        )}

        {showWorkforceTab && (
        <TabsContent value="workforce">
          {memberId && <MemberWorkforceTab memberId={memberId} />}
        </TabsContent>
        )}

        {/* History & Conflicts = Data Conflicts + Audit Trail (sub-tabs). Case
            History lives in the Activities view (TRUS-762). */}
        <TabsContent value="history">
          {memberId && activeTab === 'history' && (
            <div>
              <SubTabBar
                value={dataSub}
                onChange={(v) => setDataSub(v as 'conflicts' | 'history')}
                tabs={[
                  ...(viewSettings?.show_data_conflicts !== false
                    ? [{ id: 'conflicts', label: t('members.dataConflicts', 'Data Conflicts') }]
                    : []),
                  { id: 'history', label: t('members.auditHistory', 'Audit History') },
                ]}
              />
              {dataSub === 'conflicts' && viewSettings?.show_data_conflicts !== false ? (
                <div>{renderConflicts()}</div>
              ) : (
                <MemberHistoryPanel memberId={memberId} />
              )}
            </div>
          )}
        </TabsContent>

        {/* Engagement (TRUS-762): one entry, its segments each behind their
            own gate — Overview (engagement intelligence metrics; the record
            history is in Activities), Campaigns, Incentives, Giving (donors). */}
        <TabsContent value="engagement">
          {memberId && activeTab === 'engagement' && activeSegment && (
            <div>
              {engagementSegments.length > 1 && (
                <SubTabBar
                  value={activeSegment}
                  onChange={(v) => setEngagementSegment(v)}
                  tabs={engagementSegments}
                />
              )}
              {activeSegment === 'engagement-intel' && (
                <MemberEngagementIntelligenceTab
                  memberId={memberId}
                  cboId={parseInt(localStorage.getItem('active_cbo_id') || '0', 10) || 0}
                  hideRecentActivity
                  onSeeAllActivities={() => openTab('program-activities')}
                />
              )}
              {activeSegment === 'engagements' && (
                <MemberEngagementHistoryTab
                  memberId={memberId}
                  cboId={parseInt(localStorage.getItem('active_cbo_id') || '0', 10) || 0} />
              )}
              {activeSegment === 'achievements' && (
                <MemberAchievementsPanel
                  memberId={memberId}
                  cboId={parseInt(localStorage.getItem('active_cbo_id') || '0', 10) || undefined} />
              )}
              {activeSegment === 'giving' && <DonorGivingTab contactId={Number(memberId)} />}
            </div>
          )}
        </TabsContent>

        {/* Member setup: record upkeep that used to be cards on Member Profile. */}
        <TabsContent value="data-validation">
          {activeTab === 'data-validation' && dataValidationSection() && (
            <Card>
              <CardContent className="p-6">{dataValidationSection()!.render()}</CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="system-information">
          {activeTab === 'system-information' && systemInfoSection() && (
            <Card>
              <CardContent className="p-6">{systemInfoSection()!.render()}</CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Activities: one activity view with the case as a filter
            (case bar · type chips · feed · Add), TRUS-762. */}
        <TabsContent value="program-activities">
          {memberId && activeTab === 'program-activities' && (
            <MemberProgramWorkspace
              memberId={memberId}
              memberName={member ? `${member.first_name} ${member.last_name}` : undefined}
              member={member || undefined}
              onNavigate={onNavigate}
              show={{
                caseEpisodes: viewSettings?.show_case_episodes !== false,
                activities: viewSettings?.show_activities !== false,
                conversations: viewSettings?.show_communications !== false,
                documents: viewSettings?.show_documents !== false,
                forms: viewSettings?.show_forms !== false,
                mail: viewSettings?.show_mail !== false,
                tasks: viewSettings?.show_tasks !== false,
              }}
              // The org's wording and ordering for this bar, from the same
              // /profile-layout payload the rail uses (TRUS-528). Empty object
              // when the layout has not loaded, which reads as "all defaults".
              tabConfig={workspaceTabConfig}
              requestedWorkTab={requestedWorkTab}
            />
          )}
        </TabsContent>

        {/* One section per category an admin flagged show_as_section (TRUS-420).
            Each reads the notes tagged with it from the Activity feed — same
            rows, not a copy, so edits and untagging flow through automatically. */}
        {sectionCategories.map((c) => (
          <TabsContent key={c.id} value={`note-section-${c.id}`}>
            {memberId && activeTab === `note-section-${c.id}` && (
              <CategoryNotesSection memberId={memberId} category={c} />
            )}
          </TabsContent>
        ))}

        {/* A pinned form opens the forms hub narrowed to that template, for the
            member's enrollment in the pin's program: Continue the open draft
            if there is one, otherwise Start (TRUS-762). */}
        {pinnedFormTabs.map((tab) => (
          <TabsContent key={tab.tab_key} value={tab.tab_key}>
            {memberId && activeTab === tab.tab_key && (
              <MemberFormsHub
                key={`${tab.tab_key}-${tab.enrollment_id ?? 'none'}`}
                memberId={memberId}
                programId={tab.program_id}
                enrollmentId={tab.enrollment_id ?? undefined}
                organizationId={orgId || undefined}
                focusTemplateId={tab.form_template_id}
                focusLabel={labelFor(tab)}
              />
            )}
          </TabsContent>
        ))}

        {/* Programs (TRUS-378 P3): related list of program enrollments, each row
            expanding to the configured field set. Mounted only when active —
            it fetches field config + status config + programs on open. */}
        <TabsContent value="programs">
          {memberId && activeTab === 'programs' && (
            <MemberProgramsTab
              memberId={memberId}
              readOnly={isViewOnly}
              doulaEnabled={orgHasDoulaProgram}
              doulaRequest={doulaRequest}
              onEnrollMoreOptions={goToCreateEnrollment}
              onCountChange={setProgramsCount}
              organizationId={orgId || undefined}
            />
          )}
        </TabsContent>

        </>
        )}
              </div>
            </div>
          );
        })()}
      </Tabs>

      {/* Medicaid Enrollment Form Modal */}
      {member && (
        <MedicaidEnrollmentForm
          open={showEnrollmentForm}
          member={member}
          onClose={() => setShowEnrollmentForm(false)}
          onSuccess={handleEnrollmentSuccess}
        />
      )}

      {/* Medicaid Enrollment Detail Modal */}
      <MedicaidEnrollmentDetailModal
        open={showEnrollmentDetail}
        enrollment={selectedEnrollment}
        onClose={() => {
          setShowEnrollmentDetail(false);
          setSelectedEnrollment(null);
        }}
      />

      {/* Data Shield: Reveal Status Bar + ReauthModal */}
      <RevealStatusBar />
      <ReauthModal />
    </div>
  );
}
